/**
 * AccountSettingsPage — unified settings hub (B2C).
 *
 * Layout: full-viewport flex — left 240 px nav rail, right scrollable content.
 * Container never changes size; each panel scrolls independently.
 *
 * Tabs
 *   Profile     — Work · Preferences · Resume · Security
 *   API Keys    — ApiKeysIntegrations panel
 *   Billing     — Subscription
 *   Admin       — Analytics · Control Panel · Testimonials · Blogs · Career · Job Board (all inline, admin-only)
 */

import {
  AlertTriangle,
  Briefcase,
  Building2,
  CalendarDays,
  Camera,
  Check,
  CheckCircle2,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  Clock,
  CreditCard,
  Eye,
  EyeOff,
  FileText,
  KeyRound,
  LineChart,
  Linkedin,
  Languages,
  Lock,
  LogOut as _LogOut,
  Mail,
  Map,
  MapPin,
  MessageSquare,
  Bell,
  Pencil,
  PanelLeftClose,
  PanelLeftOpen,
  Play,
  Receipt,
  RotateCcw,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Star,
  Trash2,
  Upload,
  User,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../components/LoadingSpinner';
import { supabase } from '../lib/supabase';
import { useAuthentication } from '../hooks/useAuthentication';
import { clearUserLocalData } from '../utils/clearUserData';
import { useProductTour } from '../hooks/useProductTour';
import { FORCE_PRODUCT_TOUR_EVENT } from '../lib/productTourSteps';
import type { DashboardPage } from '../modules/dashboard/components/Layout';
import NotificationsSettingsPanel from '../modules/settings/NotificationsSettingsPanel';
import type { EmailNotificationType } from '../services/emailNotificationPreferences';

// Admin sections — lazy-loaded so they don't bloat the main settings bundle
const AdminAnalytics      = React.lazy(() => import('../modules/dashboard/components/AdminAnalytics'));
const ControlPanel        = React.lazy(() => import('../modules/dashboard/components/ControlPanel'));
const TestimonialsAdmin   = React.lazy(() => import('./admin/TestimonialsAdminPage'));
const ProductFeedbackAdmin = React.lazy(() => import('./admin/ProductFeedbackAdminPage'));
const TranslationsAdmin   = React.lazy(() => import('./admin/TranslationsAdminPage'));
const CronJobsAdmin       = React.lazy(() => import('./admin/CronJobsAdminPage'));
const BlogListPage        = React.lazy(() => import('./admin/BlogListPage'));
const CareersListPage     = React.lazy(() => import('./admin/CareersListPage'));
const JobsAdmin           = React.lazy(() => import('../modules/dashboard/components/Jobs').then(m => ({ default: m.default })));
import { PROFILE_COMPLETION_NAV_EVENT, type ProfileCompletionNavDetail } from '../modules/dashboard/components/ProfileCompletenessMeter';
import { RESUME_SECTION_FOCUS_EVENT } from '../modules/onboarding/resumeAiHelpers';
import Subscription from '../modules/dashboard/components/Subscription';
import { useUserProfile } from '../modules/dashboard/hooks/useUserProfile';
import {
  draftFromProfileRow,
  mergeLocationModesIntoDraft,
  partialUserProfileFromDraftStep,
} from '../modules/onboarding/profileDraftHelpers';
import ProfileIdentityFields from '../modules/onboarding/steps/ProfileIdentityFields';
import ProfilePreferencesFields from '../modules/onboarding/steps/ProfilePreferencesFields';
import ProfileResumeFields from '../modules/onboarding/steps/ProfileResumeFields';
import ProfileSkillsFields, { PROFILE_SKILLS_MAX } from '../modules/onboarding/steps/ProfileSkillsFields';
import {
  EMPTY_DRAFT,
  type OnboardingDraft,
  type OnboardingErrors,
} from '../modules/onboarding/types';
import { validateOnboardingStep } from '../modules/onboarding/validation/onboardingStepsValidation';
import ApiKeysIntegrations from '../modules/settings/ApiKeysIntegrations';
import PaymentHistoryPage from './PaymentHistoryPage';

// ── Admin gate ───────────────────────────────────────────────────────────────
const ADMIN_EMAIL = 'dharmendra.sp@gmail.com';

// ── Single flat section type (no nesting) ────────────────────────────────────
type SettingsSection =
  | 'profile'           // display name, emails, brief bio
  | 'work'              // job title, experience, skills, social links
  | 'preferences'       // job search preferences
  | 'resume'            // resume data
  | 'security'          // password + account
  | 'notifications'     // email opt-in/out
  | 'tour'              // app tour
  | 'api-keys'          // API keys
  | 'billing'           // subscription / billing
  | 'payment-history'   // invoices, transactions, orders
  // admin-only sections (rendered inline, URL = ?tab=<id>)
  | 'admin-analytics'
  | 'control-panel'
  | 'cron-jobs-admin'
  | 'testimonials-admin'
  | 'product-feedback-admin'
  | 'translations-admin'
  | 'blog-cms'
  | 'careers-cms'
  | 'jobs-board';

// Kept for backwards-compat where AdminSettingsLink was used as a type
type AdminSettingsLink = Extract<
  SettingsSection,
  'admin-analytics' | 'control-panel' | 'cron-jobs-admin' | 'testimonials-admin' | 'product-feedback-admin' | 'translations-admin' | 'blog-cms' | 'careers-cms' | 'jobs-board'
>;

// Keep SettingsTab as alias so existing callers (initialTab prop) still compile
type SettingsTab = SettingsSection;
// Removed: ProfileSubTab (all sections are now top-level)

interface NavItem {
  id: SettingsSection | AdminSettingsLink;
  label: string;
  labelKey?: string;
  shortLabel?: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  dividerBefore?: boolean;
  path?: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'profile',          labelKey: 'views.accountSettings.nav.profile', label: 'Profile',          icon: User        },
  { id: 'work',             labelKey: 'views.accountSettings.nav.work', label: 'Work',             icon: Briefcase   },
  { id: 'preferences',      labelKey: 'views.accountSettings.nav.preferences', label: 'Preferences',      icon: Settings    },
  { id: 'resume',           labelKey: 'views.accountSettings.nav.resume', label: 'Resume',           icon: Upload      },
  { id: 'security',         labelKey: 'views.accountSettings.nav.security', label: 'Security',         icon: Lock        },
  { id: 'notifications',    labelKey: 'views.accountSettings.nav.notifications', label: 'Notifications',    shortLabel: 'Alerts', icon: Bell },
  { id: 'billing',          labelKey: 'views.accountSettings.nav.billing', label: 'Billing',          icon: CreditCard  },
  { id: 'payment-history',  labelKey: 'views.accountSettings.nav.paymentHistory', label: 'Payment History',  shortLabel: 'Payments', icon: Receipt },
  // Admin-only sections — rendered inline, no path redirect
  { id: 'admin-analytics',    label: 'Admin Analytics', shortLabel: 'Analytics', icon: LineChart, adminOnly: true, dividerBefore: true },
  { id: 'api-keys',           label: 'API Keys',        icon: KeyRound,           adminOnly: true },
  { id: 'control-panel',      label: 'Control Panel',   shortLabel: 'CRM',     icon: SlidersHorizontal,  adminOnly: true },
  { id: 'cron-jobs-admin',    label: 'Cron Jobs',       shortLabel: 'Cron',    icon: Clock,              adminOnly: true },
  { id: 'testimonials-admin', label: 'Testimonials',    icon: MessageSquare,      adminOnly: true },
  { id: 'product-feedback-admin', label: 'Product Feedback', shortLabel: 'Feedback', icon: ClipboardList, adminOnly: true },
  { id: 'translations-admin', label: 'Translations', icon: Languages,          adminOnly: true },
  { id: 'blog-cms',           label: 'Blogs',           icon: FileText,           adminOnly: true },
  { id: 'careers-cms',        label: 'Career',          icon: Building2,          adminOnly: true },
  { id: 'jobs-board',         label: 'Job Board',       shortLabel: 'Jobs',    icon: Briefcase,          adminOnly: true },
];

// ── Tour steps summary ────────────────────────────────────────────────────────
const TOUR_STEPS_SUMMARY = [
  'Welcome & workspace overview',
  'Sidebar navigation',
  'Smart status bar',
  'Progress KPI tiles',
  'Jobs matched to your profile',
  'Browse all jobs',
  'Resume Optimizer',
  'Activity log',
  'AI Agent chat',
  'Skill Assessment',
  'AI Companion (always-on helper)',
  'Referrals & rewards',
  'Credits balance',
  'Final — your next smart step',
];

// ── Helpers ──────────────────────────────────────────────────────────────────
function nameInitials(email: string | null | undefined) {
  return email ? email[0].toUpperCase() : 'U';
}

// ── Shared mini-components ───────────────────────────────────────────────────
const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <section className={`ds-card space-y-4 ${className}`}>
    {children}
  </section>
);

const SectionSaveBar: React.FC<{
  title: string;
  desc?: string;
  icon?: React.ReactNode;
  step: 1 | 2 | 3 | 4;
  isSaving: boolean;
  onSave: (step: 1 | 2 | 3 | 4) => void;
}> = ({ title, desc, icon, step, isSaving, onSave }) => (
  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-2">
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        {icon && <span style={{ color: 'var(--ds-brand)' }}>{icon}</span>}
        <h3 className="t-card-title font-semibold" style={{ color: 'var(--ds-text-strong)' }}>{title}</h3>
      </div>
      {desc && <p className="t-meta mt-0.5" style={{ color: 'var(--ds-text-muted)' }}>{desc}</p>}
    </div>
    <button
      type="button"
      disabled={isSaving}
      onClick={() => onSave(step)}
      className="shrink-0 min-h-[44px] w-full sm:w-auto px-4 rounded-lg t-body font-medium transition-colors disabled:opacity-50 touch-manipulation"
      style={{ border: '1px solid rgba(108,71,255,0.5)', color: 'var(--ds-brand)' }}
      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(108,71,255,0.08)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      {isSaving ? 'Saving…' : 'Save'}
    </button>
  </div>
);

// ── Handle extraction helpers for social links ───────────────────────────────
function extractLinkedInHandle(url: string): string {
  if (!url) return '';
  const m = url.match(/linkedin\.com\/in\/([^/?#\s]+)/i);
  return m ? m[1] : (url.startsWith('http') ? '' : url);
}
function extractGithubHandle(url: string): string {
  if (!url) return '';
  const m = url.match(/github\.com\/([^/?#\s]+)/i);
  return m ? m[1] : (url.startsWith('http') ? '' : url);
}

// ── Compact inline field (no floating label, cleaner look) ───────────────────
const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  error?: string | null;
  hint?: string;
  required?: boolean;
  rows?: number;
  prefix?: string;
  suffix?: React.ReactNode;
  maxLength?: number;
  select?: { label: string; value: string }[];
}> = ({ label, value, onChange, placeholder, type = 'text', error, hint, required, rows, prefix, suffix, maxLength, select }) => {
  const { t } = useTranslation();
  const fieldBorderColor = error ? 'rgba(239,68,68,0.6)' : 'var(--ds-border-strong)';
  const focusStyle = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const el = e.currentTarget.closest('[data-field-wrap]') as HTMLElement | null;
    if (el) { el.style.borderColor = 'var(--ds-brand)'; el.style.boxShadow = '0 0 0 3px rgba(108,71,255,0.10)'; }
  };
  const blurStyle = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const el = e.currentTarget.closest('[data-field-wrap]') as HTMLElement | null;
    if (el) { el.style.borderColor = error ? 'rgba(239,68,68,0.6)' : 'var(--ds-border-strong)'; el.style.boxShadow = '0 1px 2px 0 rgba(0,0,0,0.05)'; }
  };
  return (
  <div className="w-full">
    {label && (
    <label className="ds-field-label">
      <span>{label}{required && <span className="t-danger ml-0.5">*</span>}</span>
      {maxLength && <span className="t-tag t-num" style={{ color: value.length > maxLength * 0.9 ? 'var(--ds-warn)' : 'var(--ds-text-disabled)' }}>{value.length}</span>}
    </label>
    )}
    <div
      data-field-wrap
      className="ds-field-control"
      style={{
        borderColor: fieldBorderColor,
      }}
    >
      {prefix && (
        <span className="ds-field-prefix">
          {prefix}
        </span>
      )}
      {select ? (
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={focusStyle} onBlur={blurStyle}
          className="ds-field-input appearance-none cursor-pointer"
        >
          <option value="">{t('views.accountSettings.common.select', 'Select…')}</option>
          {select.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : rows ? (
        <textarea
          rows={rows}
          value={value}
          maxLength={maxLength}
          onChange={e => onChange(e.target.value)}
          onFocus={focusStyle} onBlur={blurStyle}
          placeholder={placeholder}
          className="ds-field-input"
        />
      ) : (
        <input
          type={type}
          value={value}
          maxLength={maxLength}
          onChange={e => onChange(e.target.value)}
          onFocus={focusStyle} onBlur={blurStyle}
          placeholder={placeholder}
          className="ds-field-input"
        />
      )}
      {suffix}
    </div>
    {error && <p className="mt-1 t-tag text-red-400">{error}</p>}
    {hint && !error && <p className="mt-1 t-tag" style={{ color: 'var(--ds-text-disabled)' }}>{hint}</p>}
  </div>
  );
};

// ── Section divider header ────────────────────────────────────────────────────
const SectionHead: React.FC<{ icon: React.ReactNode; label: string; centered?: boolean }> = ({ icon, label, centered }) => (
  centered ? (
    <div className="flex items-center justify-center gap-2 py-3 mb-5 border-y" style={{ borderColor: 'var(--ds-border)' }}>
      <span style={{ color: 'var(--ds-text-muted)' }}>{icon}</span>
      <span className="t-tag font-bold uppercase tracking-widest" style={{ color: 'var(--ds-text-muted)' }}>{label}</span>
    </div>
  ) : (
    <div className="flex items-center gap-2 py-2 mb-4 border-b" style={{ borderColor: 'var(--ds-border)' }}>
      <span style={{ color: 'var(--ds-text-muted)' }}>{icon}</span>
      <span className="t-tag font-semibold uppercase tracking-widest" style={{ color: 'var(--ds-text-muted)' }}>{label}</span>
    </div>
  )
);

// ── Inline avatar upload (compact, image-3 style) ─────────────────────────────
const AvatarUpload: React.FC<{ avatarUrl: string | null; userEmail: string }> = ({ avatarUrl, userEmail }) => {
  const [uploading, setUploading] = useState(false);
  const [localUrl, setLocalUrl] = useState<string | null>(avatarUrl);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setLocalUrl(avatarUrl); }, [avatarUrl]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setErr('Max 2 MB'); return; }
    setUploading(true); setErr(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const ext = file.name.split('.').pop() ?? 'jpg';
      const path = `avatars/${user.id}.${ext}`;
      const { error: upErr } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
      setLocalUrl(urlData.publicUrl + '?t=' + Date.now());
      await supabase.from('user_profiles').upsert({ id: user.id, avatar_url: urlData.publicUrl }, { onConflict: 'id' });
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4 pb-5 mb-5 border-b" style={{ borderColor: 'var(--ds-border)' }}>
      <div className="relative shrink-0">
        <div className="w-[72px] h-[72px] rounded-full overflow-hidden flex items-center justify-center t-h2 font-bold"
          style={{ background: 'var(--ds-brand)', color: 'var(--ds-text-on-brand)' }}>
          {localUrl
            ? <img src={localUrl} alt="Your profile photo" className="w-full h-full object-cover" />
            : nameInitials(userEmail)}
        </div>
      </div>
      <div>
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1.5 min-h-[30px] px-3 rounded-lg t-meta font-medium border transition-colors disabled:opacity-50"
          style={{ color: 'var(--ds-brand)', borderColor: 'rgba(108,71,255,0.4)' } as React.CSSProperties}
        >
          {uploading ? <LoadingSpinner size="xs" showText={false} /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? 'Uploading…' : 'Upload new'}
        </button>
        <p className="mt-1 t-tag t-num" style={{ color: 'var(--ds-text-muted)' }}>Recommended size: 400×400 px · max 2 MB</p>
        {err && <p className="mt-0.5 t-tag text-red-400">{err}</p>}
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} className="hidden" />
      </div>
    </div>
  );
};

// ── Password change ───────────────────────────────────────────────────────────
const SECURITY_FONT = 'var(--ds-font-sans)';
const SECURITY_INPUT_SHADOW = '0 1px 2px 0 rgba(0,0,0,0.05)';
const SECURITY_INPUT_FOCUS_SHADOW = '0 0 0 4px rgba(99,102,241,0.1), 0 1px 2px 0 rgba(0,0,0,0.05)';

const securityInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
  e.currentTarget.style.borderColor = 'var(--ds-brand)';
  e.currentTarget.style.boxShadow = SECURITY_INPUT_FOCUS_SHADOW;
};
const securityInputBlur = (e: React.FocusEvent<HTMLInputElement>, borderColor = 'var(--ds-border-strong)') => {
  e.currentTarget.style.borderColor = borderColor;
  e.currentTarget.style.boxShadow = SECURITY_INPUT_SHADOW;
};

const SECURITY_INPUT_CLASS =
  'w-full py-[10px] pl-[14px] pr-12 rounded-lg t-body outline-none transition-all duration-200 ease-in-out';

const SECURITY_BTN_CLASS = 'py-[10px] px-[14px] rounded-lg t-body font-medium transition-all duration-200 ease-in-out';

const PasswordChangeSection: React.FC = () => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const handleSave = async () => {
    if (next !== confirm) { setMsg({ kind: 'err', text: t('views.accountSettings.security.passwordsDoNotMatch', 'Passwords do not match.') }); return; }
    if (next.length < 8)  { setMsg({ kind: 'err', text: t('views.accountSettings.security.passwordMinLength', 'Password must be at least 8 characters.') }); return; }
    if (!current)         { setMsg({ kind: 'err', text: t('views.accountSettings.security.enterCurrentPassword', 'Enter your current password.') }); return; }
    setSaving(true); setMsg(null);
    const { error } = await supabase.auth.updateUser({ password: next });
    setSaving(false);
    if (error) setMsg({ kind: 'err', text: error.message });
    else { setMsg({ kind: 'ok', text: t('views.accountSettings.security.passwordUpdatedSuccess', 'Password updated successfully.') }); setCurrent(''); setNext(''); setConfirm(''); }
  };

  const pwdFocus = securityInputFocus;
  const pwdBlur = (e: React.FocusEvent<HTMLInputElement>) => securityInputBlur(e);

  const PasswordField: React.FC<{
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    show: boolean;
    onToggleShow: () => void;
    required?: boolean;
  }> = ({ label, value, onChange, placeholder, show, onToggleShow, required }) => (
    <div className="w-full">
      <label className="block mb-1.5 t-body">
        {label}{required && <span className="t-danger ml-0.5">*</span>}
      </label>
      <div className="password-input-wrapper relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          onFocus={pwdFocus}
          onBlur={pwdBlur}
          className={SECURITY_INPUT_CLASS}
          style={{
            border: '1px solid var(--ds-border-strong)',
            boxShadow: SECURITY_INPUT_SHADOW,
            borderRadius: '8px',
            background: 'var(--ds-bg-card)',
            fontFamily: SECURITY_FONT,
          }}
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="password-toggle-icon absolute right-[14px] top-1/2 -translate-y-1/2 cursor-pointer transition-colors"
          style={{ color: 'var(--ds-text-muted)' }}
          aria-label={show ? t('views.accountSettings.security.hidePassword', 'Hide password') : t('views.accountSettings.security.showPassword', 'Show password')}
        >
          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <PasswordField label={t('views.accountSettings.security.currentPassword', 'Current password')} value={current} onChange={setCurrent} placeholder="••••••••" show={showCurrent} onToggleShow={() => setShowCurrent(v => !v)} required />
      <PasswordField label={t('views.accountSettings.security.newPassword', 'New password')} value={next} onChange={setNext} placeholder={t('views.accountSettings.security.newPasswordPlaceholder', 'Min 8 characters')} show={showNext} onToggleShow={() => setShowNext(v => !v)} required />
      <PasswordField label={t('views.accountSettings.security.confirmPassword', 'Confirm new password')} value={confirm} onChange={setConfirm} placeholder={t('views.accountSettings.security.confirmPasswordPlaceholder', 'Repeat password')} show={showConfirm} onToggleShow={() => setShowConfirm(v => !v)} required />
      {msg && (
        <p className="flex items-center gap-1.5 t-body" style={{ color: msg.kind === 'ok' ? 'var(--ds-ok)' : 'var(--ds-danger)' }}>
          {msg.kind === 'ok' ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          {msg.text}
        </p>
      )}
      <button
        type="button"
        disabled={saving || !next || !confirm || !current}
        onClick={() => void handleSave()}
        className="ds-btn ds-btn-primary disabled:opacity-50"
        style={{ fontFamily: SECURITY_FONT }}
      >
        {saving ? t('views.accountSettings.security.saving', 'Saving…') : t('views.accountSettings.security.updatePassword', 'Update password')}
      </button>
    </div>
  );
};

// ── Account management (GDPR + delete) ───────────────────────────────────────
const normalizeAccountEmail = (email: string) => email.trim().toLowerCase();

const resolveAuthUserEmail = (emailFromAuth: string | undefined, metadata: unknown): string => {
  if (emailFromAuth?.trim()) return emailFromAuth.trim();
  const meta = metadata as { email?: string } | undefined;
  return meta?.email?.trim() ?? '';
};

const emailsMatchForDeletion = (typed: string, accountEmail: string) => {
  const normalizedAccount = normalizeAccountEmail(accountEmail);
  if (!normalizedAccount) return false;
  return normalizeAccountEmail(typed) === normalizedAccount;
};

const AccountManagementSection: React.FC<{ userEmail: string; onSignOut: () => void }> = ({ userEmail, onSignOut }) => {
  const { t } = useTranslation();
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [accountEmail, setAccountEmail] = useState(userEmail.trim());
  const [deleting, setDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState<string | null>(null);
  const deleteInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    const loadAccountEmail = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (cancelled || !user) return;
        const resolved = resolveAuthUserEmail(user.email, user.user_metadata);
        if (resolved) setAccountEmail(resolved);
      } catch {
        // keep prop fallback
      }
    };
    void loadAccountEmail();
    return () => { cancelled = true; };
  }, [userEmail]);

  useEffect(() => {
    if (userEmail.trim()) setAccountEmail(userEmail.trim());
  }, [userEmail]);

  const canDelete = !deleting && accountEmail.length > 0 && emailsMatchForDeletion(deleteConfirm, accountEmail);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase.from('user_profiles').select('*').eq('id', user.id).single();
      const { data: keys } = await supabase.from('api_keys').select('id,key_prefix,label,scopes,created_at,last_used_at').eq('user_id', user.id);
      const blob = new Blob([JSON.stringify({ exported_at: new Date().toISOString(), user: { id: user.id, email: user.email }, profile, api_keys: keys }, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `gograbjob-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
    } finally { setExportLoading(false); }
  };

  const handleDelete = async () => {
    const confirmedEmail = (deleteInputRef.current?.value ?? deleteConfirm).trim();
    if (!emailsMatchForDeletion(confirmedEmail, accountEmail)) {
      setDeleteMsg(accountEmail
        ? t('views.accountSettings.security.typeEmailExactly', {
            defaultValue: 'Type your account email exactly: {{email}}',
            email: accountEmail,
          })
        : t('views.accountSettings.security.noLoginEmail', 'No login email on this account. Contact support to delete.'));
      return;
    }

    setDeleting(true);
    setDeleteMsg(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error(t('views.accountSettings.security.authRequired', 'Authentication required. Sign in again and retry.'));

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user-data`;
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deletionData: {
            reasons: [],
            feedback: '',
            rating: null,
            ratingComment: '',
            wantsReconnect: false,
            email: accountEmail,
            alternativeConsidered: false,
          },
          confirmDeletion: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          (errorData as { error?: string }).error || t('views.accountSettings.security.deleteFailed', 'Failed to delete account. Please try again or contact support.'),
        );
      }

      supabase.functions.invoke('send-funnel-email', {
        body: { type: 'account_deleted', email: accountEmail },
      }).catch(() => {});

      clearUserLocalData();
      sessionStorage.clear();
      localStorage.clear();

      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      onSignOut();
      window.location.href = '/';
    } catch (err) {
      setDeleteMsg(err instanceof Error ? err.message : t('views.accountSettings.security.deleteFailedShort', 'Failed to delete account.'));
      setDeleting(false);
    }
  };

  const cardInputFocus = securityInputFocus;
  const cardInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    securityInputBlur(e, 'var(--ds-danger-border)');
    setDeleteConfirm(e.target.value);
  };

  return (
    <div className="space-y-4">
      {/* Export card */}
      <div className="ds-card">
        <h4 className="t-card-title font-semibold" style={{ color: 'var(--ds-text-strong)' }}>{t('views.accountSettings.security.exportTitle', 'Export your data')}</h4>
        <p className="t-body font-normal mt-1 mb-4 leading-normal" style={{ color: 'var(--ds-text-body)' }}>
          {t('views.accountSettings.security.exportDesc', 'Download a JSON copy of your profile and API keys (GDPR Art. 20 — data portability).')}
        </p>
        <button
          type="button"
          disabled={exportLoading}
          onClick={() => void handleExport()}
          className="ds-btn ds-btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {exportLoading ? <LoadingSpinner size="xs" showText={false} /> : null}
          {exportLoading ? t('views.accountSettings.security.exporting', 'Exporting…') : t('views.accountSettings.security.downloadMyData', 'Download my data')}
        </button>
      </div>

      {/* Delete account card */}
      <div className="ds-card space-y-3" style={{ borderColor: 'var(--ds-danger-border)' }}>
        <div className="flex items-center gap-2">
          <Trash2 className="w-4 h-4" style={{ color: 'var(--ds-danger)' }} />
          <h4 className="t-card-title font-semibold" style={{ color: 'var(--ds-danger)' }}>{t('views.accountSettings.security.deleteAccount', 'Delete account')}</h4>
        </div>
        <p className="t-body font-normal leading-normal" style={{ color: 'var(--ds-text-body)' }}>
          {t('views.accountSettings.security.deleteAccountDesc', {
            defaultValue: 'Permanently deletes your account. Type your login email to confirm{{emailSuffix}}.',
            emailSuffix: accountEmail ? ` (${accountEmail})` : '',
          })}
        </p>
        <input
          ref={deleteInputRef}
          type="email"
          name="confirm-account-deletion"
          autoComplete="off"
          value={deleteConfirm}
          onChange={e => { setDeleteConfirm(e.target.value); setDeleteMsg(null); }}
          onInput={e => { setDeleteConfirm(e.currentTarget.value); setDeleteMsg(null); }}
          placeholder={accountEmail || t('views.accountSettings.security.deleteEmailPlaceholder', 'your@email.com')}
          onFocus={cardInputFocus}
          onBlur={cardInputBlur}
          className={SECURITY_INPUT_CLASS}
          style={{
            border: '1px solid var(--ds-danger-border)',
            boxShadow: SECURITY_INPUT_SHADOW,
            borderRadius: '8px',
            background: 'var(--ds-bg-card)',
          }}
        />
        {deleteMsg && (
          <p className="t-body flex items-center gap-1.5" style={{ color: 'var(--ds-danger)' }}>
            <AlertTriangle className="w-3.5 h-3.5" />{deleteMsg}
          </p>
        )}
        <button
          type="button"
          disabled={!canDelete}
          onClick={() => void handleDelete()}
          className="ds-btn ds-btn-danger disabled:opacity-50"
        >
          {deleting ? t('views.accountSettings.security.deleting', 'Deleting…') : t('views.accountSettings.security.deleteMyAccount', 'Delete my account')}
        </button>
      </div>
    </div>
  );
};


// ── Reusable settings tab layout (Untitled UI canvas template) ───────────────
const SettingsTabCanvas: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`ds-settings-tab space-y-8 max-w-6xl pb-10 ${className}`}>
    {children}
  </div>
);

const AdminSectionFallback = () => (
  <div className="flex items-center justify-center py-16">
    <LoadingSpinner size="lg" showText={false} />
  </div>
);

/**
 * Admin sections that are mounted once (on first visit) and then kept alive
 * (hidden via CSS) instead of being remounted on every tab switch. `render`
 * receives whether the section is currently the active tab, for components that
 * defer work when hidden. Control Panel is rendered by its own dedicated block.
 */
const ADMIN_PERSISTENT_SECTIONS: {
  id: SettingsSection;
  render: (active: boolean) => React.ReactNode;
}[] = [
  { id: 'admin-analytics',    render: () => <AdminAnalytics embedded /> },
  { id: 'cron-jobs-admin',    render: (active) => <CronJobsAdmin embedded isActive={active} /> },
  { id: 'testimonials-admin', render: () => <TestimonialsAdmin embedded /> },
  { id: 'product-feedback-admin', render: () => <ProductFeedbackAdmin embedded /> },
  { id: 'translations-admin', render: () => <TranslationsAdmin embedded /> },
  { id: 'blog-cms',           render: () => <BlogListPage embedded /> },
  { id: 'careers-cms',        render: () => <CareersListPage embedded /> },
  { id: 'jobs-board',         render: () => <JobsAdmin onNavigate={() => {}} mode="admin" /> },
];

/** All admin section ids (persistent list + Control Panel) — these skip renderContent(). */
const ADMIN_SECTION_IDS = new Set<SettingsSection>([
  'control-panel',
  ...ADMIN_PERSISTENT_SECTIONS.map(s => s.id),
]);

const SettingsTextHeader: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="pb-5 border-b" style={{ borderColor: 'var(--ds-divider)' }}>
    <h1 className="t-page-title">{title}</h1>
    <p className="t-meta mt-1">{description}</p>
  </div>
);

const SettingsTabRow: React.FC<{
  label: string;
  desc?: string;
  children: React.ReactNode;
  wide?: boolean;
  uncapped?: boolean;
  noBorder?: boolean;
}> = ({ label, desc, children, wide, uncapped, noBorder }) => (
  <div className={`ds-form-row ${noBorder ? 'ds-form-row-no-border' : ''}`}>
    <div className="ds-form-row-copy">
      <h3 className="t-section">{label}</h3>
      {desc && <p className="t-meta mt-1 leading-relaxed">{desc}</p>}
    </div>
    <div className={`ds-form-row-control space-y-4 ${uncapped ? '!max-w-none' : wide ? '!max-w-[720px]' : ''}`}>{children}</div>
  </div>
);

// ── Profile banner — gradient strip + avatar circle with pencil ──────────────
// ── Section meta map ─────────────────────────────────────────────────────────
const SECTION_META: Record<SettingsSection, { title: string; desc: string }> = {
  profile:              { title: 'Profile',          desc: 'Update your photo and personal details.' },
  work:                 { title: 'Work',             desc: 'Your job title, experience, skills and social links.' },
  preferences:          { title: 'Preferences',      desc: 'Set your job search status, target roles and preferences.' },
  resume:               { title: 'Resume',           desc: 'Your work history, education and skills.' },
  security:             { title: 'Security',         desc: 'Password and account management.' },
  notifications:        { title: 'Notifications',    desc: 'Choose which emails you receive from GoGrabJob.' },
  tour:                 { title: 'App Tour',         desc: 'Replay the guided product tour anytime.' },
  'api-keys':           { title: 'API Keys',         desc: 'Personal access tokens, AI models and job channel connections.' },
  billing:              { title: 'Billing',          desc: 'Subscription, credits and payment history.' },
  'payment-history':    { title: 'Payment History',  desc: 'Invoices, transactions, orders and usage history.' },
  // Admin sections
  'admin-analytics':    { title: 'Admin Analytics',  desc: 'Platform KPIs, user metrics and revenue overview.' },
  'control-panel':      { title: 'Control Panel',    desc: 'Feature flags, system configuration and admin controls.' },
  'cron-jobs-admin':    { title: 'Cron Jobs',        desc: 'Scheduled pg_cron jobs, execution history, and manual triggers.' },
  'testimonials-admin': { title: 'Testimonials',     desc: 'Manage user testimonials and social proof.' },
  'product-feedback-admin': { title: 'Product Feedback', desc: 'Review survey responses and email invite campaigns.' },
  'translations-admin': { title: 'Translations',     desc: 'Manage UI strings across locales (NLS database).' },
  'blog-cms':           { title: 'Blogs',            desc: 'Create, edit and publish blog posts.' },
  'careers-cms':        { title: 'Career',           desc: 'Create job openings and publish them to the public careers page.' },
  'jobs-board':         { title: 'Job Board',        desc: 'Manage and moderate the jobs board.' },
};

// ── ProfileBanner ─────────────────────────────────────────────────────────────
// Full-width gradient strip + large avatar (96px, white ring) + title/desc
const ProfileBanner: React.FC<{
  avatarUrl: string | null;
  userEmail: string;
  onPhotoClick: () => void;
  uploading: boolean;
  fileRef: React.RefObject<HTMLInputElement>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  uploadErr: string | null;
  title: string;
  description: string;
  isSaving?: boolean;
}> = ({ avatarUrl, userEmail, onPhotoClick, uploading, fileRef, onFileChange, uploadErr, title, description }) => {
  const { t } = useTranslation();
  return (
  <div className="ds-profile-banner">
    <div className="ds-profile-banner__cover" aria-hidden />

    <div className="ds-profile-banner__row">
      <div className="relative shrink-0">
        <div className="ds-profile-banner__avatar">
          {avatarUrl
            ? <img src={avatarUrl} alt={t('views.accountSettings.profile.photoAlt', 'Profile photo')} className="w-full h-full object-cover" />
            : nameInitials(userEmail)}
        </div>
        {/* Pencil badge */}
        <button
          type="button"
          disabled={uploading}
          onClick={onPhotoClick}
          title={t('views.accountSettings.profile.changePhoto', 'Change photo')}
          className="ds-profile-banner__photo-btn disabled:opacity-50 touch-manipulation"
        >
          {uploading
            ? <LoadingSpinner size="xs" showText={false} />
            : <Pencil className="w-3.5 h-3.5" />}
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFileChange} className="hidden" />
      </div>

      {/* Title + description (left) */}
      <div className="flex-1 min-w-0 pb-3">
        <h2 className="t-h2 font-semibold leading-tight" style={{ color: 'var(--ds-text-strong)' }}>{title}</h2>
        <p className="t-body mt-1" style={{ color: 'var(--ds-text-muted)' }}>{description}</p>
      </div>

      {/* Save buttons moved to SettingsSaveFooter at bottom of each tab */}
    </div>

    {uploadErr && (
      <p className="mt-1.5 px-8 t-tag text-red-400">{uploadErr}</p>
    )}

    <div className="ds-profile-banner__separator" />
  </div>
  );
};

// ── Global cities + countries dataset (autocomplete) ─────────────────────────
const LOCATION_DATA: string[] = [
  // ── Work modes ───────────────────────────────────────────────────────────
  'Remote','Remote (Global)','Anywhere',

  // ── South Asia ───────────────────────────────────────────────────────────
  'Mumbai, India','Delhi, India','Bengaluru, India','Hyderabad, India','Chennai, India','Kolkata, India',
  'Pune, India','Ahmedabad, India','Jaipur, India','Surat, India','Lucknow, India','Kanpur, India',
  'Nagpur, India','Indore, India','Thane, India','Bhopal, India','Visakhapatnam, India','Patna, India',
  'Vadodara, India','Ghaziabad, India','Ludhiana, India','Agra, India','Nashik, India','Faridabad, India',
  'Meerut, India','Rajkot, India','Varanasi, India','Srinagar, India','Aurangabad, India',
  'Noida, India','Gurgaon, India','Chandigarh, India','Coimbatore, India','Kochi, India',
  'Mysuru, India','Thiruvananthapuram, India','Bhubaneswar, India','Dehradun, India','Guwahati, India',
  'Karachi, Pakistan','Lahore, Pakistan','Islamabad, Pakistan','Rawalpindi, Pakistan','Faisalabad, Pakistan',
  'Multan, Pakistan','Peshawar, Pakistan','Quetta, Pakistan','Sialkot, Pakistan','Hyderabad, Pakistan',
  'Dhaka, Bangladesh','Chittagong, Bangladesh','Sylhet, Bangladesh','Khulna, Bangladesh','Rajshahi, Bangladesh',
  'Colombo, Sri Lanka','Kandy, Sri Lanka','Galle, Sri Lanka','Jaffna, Sri Lanka',
  'Kathmandu, Nepal','Pokhara, Nepal','Lalitpur, Nepal','Bharatpur, Nepal',
  'Kabul, Afghanistan','Kandahar, Afghanistan','Herat, Afghanistan','Mazar-i-Sharif, Afghanistan',
  'Thimphu, Bhutan','Phuntsholing, Bhutan',
  'Male, Maldives',

  // ── East Asia ────────────────────────────────────────────────────────────
  'Tokyo, Japan','Osaka, Japan','Nagoya, Japan','Sapporo, Japan','Fukuoka, Japan','Yokohama, Japan',
  'Kobe, Japan','Kyoto, Japan','Kawasaki, Japan','Hiroshima, Japan','Sendai, Japan','Kitakyushu, Japan',
  'Beijing, China','Shanghai, China','Guangzhou, China','Shenzhen, China','Chengdu, China',
  'Hangzhou, China','Wuhan, China',"Xi'an, China",'Nanjing, China','Chongqing, China',
  'Tianjin, China','Dongguan, China','Foshan, China','Zhengzhou, China','Shenyang, China',
  'Qingdao, China','Harbin, China','Dalian, China','Changsha, China','Kunming, China',
  'Seoul, South Korea','Busan, South Korea','Incheon, South Korea','Daegu, South Korea',
  'Daejeon, South Korea','Gwangju, South Korea','Suwon, South Korea','Ulsan, South Korea',
  'Taipei, Taiwan','Kaohsiung, Taiwan','Taichung, Taiwan','Tainan, Taiwan','Hsinchu, Taiwan',
  'Hong Kong','Macau',
  'Ulaanbaatar, Mongolia',
  'Pyongyang, North Korea',

  // ── Southeast Asia ───────────────────────────────────────────────────────
  'Singapore',
  'Kuala Lumpur, Malaysia','Penang, Malaysia','Johor Bahru, Malaysia','Kota Kinabalu, Malaysia','Kuching, Malaysia',
  'Bangkok, Thailand','Chiang Mai, Thailand','Pattaya, Thailand','Phuket, Thailand','Nonthaburi, Thailand',
  'Manila, Philippines','Quezon City, Philippines','Cebu City, Philippines','Davao, Philippines','Makati, Philippines',
  'Jakarta, Indonesia','Surabaya, Indonesia','Bandung, Indonesia','Medan, Indonesia','Semarang, Indonesia',
  'Bekasi, Indonesia','Depok, Indonesia','Palembang, Indonesia','Tangerang, Indonesia','Makassar, Indonesia',
  'Ho Chi Minh City, Vietnam','Hanoi, Vietnam','Da Nang, Vietnam','Can Tho, Vietnam','Haiphong, Vietnam',
  'Yangon, Myanmar','Mandalay, Myanmar','Naypyidaw, Myanmar',
  'Phnom Penh, Cambodia','Siem Reap, Cambodia',
  'Vientiane, Laos','Luang Prabang, Laos',
  'Bandar Seri Begawan, Brunei',
  'Dili, Timor-Leste',

  // ── Central Asia ─────────────────────────────────────────────────────────
  'Tashkent, Uzbekistan','Samarkand, Uzbekistan','Namangan, Uzbekistan','Andijan, Uzbekistan',
  'Almaty, Kazakhstan','Nur-Sultan, Kazakhstan','Shymkent, Kazakhstan','Karaganda, Kazakhstan',
  'Bishkek, Kyrgyzstan','Osh, Kyrgyzstan',
  'Dushanbe, Tajikistan','Khujand, Tajikistan',
  'Ashgabat, Turkmenistan','Turkmenbashi, Turkmenistan',

  // ── Middle East ──────────────────────────────────────────────────────────
  'Dubai, UAE','Abu Dhabi, UAE','Sharjah, UAE','Ajman, UAE','Ras Al Khaimah, UAE','Fujairah, UAE',
  'Riyadh, Saudi Arabia','Jeddah, Saudi Arabia','Mecca, Saudi Arabia','Medina, Saudi Arabia',
  'Dammam, Saudi Arabia','Khobar, Saudi Arabia','Tabuk, Saudi Arabia',
  'Doha, Qatar','Al Wakrah, Qatar',
  'Kuwait City, Kuwait','Hawalli, Kuwait','Salmiya, Kuwait',
  'Manama, Bahrain','Muharraq, Bahrain',
  'Muscat, Oman','Salalah, Oman','Seeb, Oman',
  'Tel Aviv, Israel','Jerusalem, Israel','Haifa, Israel','Rishon LeZion, Israel','Petah Tikva, Israel',
  'Amman, Jordan','Zarqa, Jordan','Irbid, Jordan',
  'Beirut, Lebanon','Tripoli, Lebanon','Sidon, Lebanon',
  'Damascus, Syria','Aleppo, Syria','Homs, Syria',
  'Baghdad, Iraq','Basra, Iraq','Mosul, Iraq','Erbil, Iraq',
  'Tehran, Iran','Mashhad, Iran','Isfahan, Iran','Karaj, Iran','Tabriz, Iran','Shiraz, Iran',
  'Sanaa, Yemen','Aden, Yemen',

  // ── Europe — Western ─────────────────────────────────────────────────────
  'London, UK','Manchester, UK','Birmingham, UK','Leeds, UK','Glasgow, UK','Edinburgh, UK',
  'Bristol, UK','Liverpool, UK','Sheffield, UK','Cardiff, UK','Leicester, UK','Coventry, UK',
  'Newcastle, UK','Nottingham, UK','Southampton, UK','Brighton, UK','Reading, UK','Oxford, UK','Cambridge, UK',
  'Paris, France','Lyon, France','Marseille, France','Toulouse, France','Nice, France','Bordeaux, France',
  'Nantes, France','Strasbourg, France','Montpellier, France','Lille, France','Rennes, France',
  'Berlin, Germany','Munich, Germany','Hamburg, Germany','Frankfurt, Germany','Cologne, Germany',
  'Stuttgart, Germany','Düsseldorf, Germany','Leipzig, Germany','Dortmund, Germany','Essen, Germany',
  'Bremen, Germany','Hanover, Germany','Nuremberg, Germany','Dresden, Germany','Bonn, Germany',
  'Madrid, Spain','Barcelona, Spain','Valencia, Spain','Seville, Spain','Bilbao, Spain',
  'Malaga, Spain','Zaragoza, Spain','Murcia, Spain','Palma, Spain','Las Palmas, Spain',
  'Rome, Italy','Milan, Italy','Naples, Italy','Turin, Italy','Florence, Italy','Venice, Italy',
  'Bologna, Italy','Genoa, Italy','Palermo, Italy','Catania, Italy','Bari, Italy','Verona, Italy',
  'Amsterdam, Netherlands','Rotterdam, Netherlands','The Hague, Netherlands','Utrecht, Netherlands',
  'Eindhoven, Netherlands','Groningen, Netherlands','Tilburg, Netherlands',
  'Brussels, Belgium','Antwerp, Belgium','Ghent, Belgium','Charleroi, Belgium','Bruges, Belgium',
  'Zurich, Switzerland','Geneva, Switzerland','Bern, Switzerland','Basel, Switzerland','Lausanne, Switzerland',
  'Vienna, Austria','Graz, Austria','Linz, Austria','Salzburg, Austria','Innsbruck, Austria',
  'Lisbon, Portugal','Porto, Portugal','Braga, Portugal','Coimbra, Portugal','Funchal, Portugal',
  'Stockholm, Sweden','Gothenburg, Sweden','Malmö, Sweden','Uppsala, Sweden','Västerås, Sweden',
  'Oslo, Norway','Bergen, Norway','Trondheim, Norway','Stavanger, Norway',
  'Copenhagen, Denmark','Aarhus, Denmark','Odense, Denmark','Aalborg, Denmark',
  'Helsinki, Finland','Tampere, Finland','Turku, Finland','Espoo, Finland','Oulu, Finland',
  'Reykjavik, Iceland',
  'Dublin, Ireland','Cork, Ireland','Limerick, Ireland','Galway, Ireland',
  'Athens, Greece','Thessaloniki, Greece','Patras, Greece','Heraklion, Greece',
  'Luxembourg City, Luxembourg',
  'Monaco',
  'Valletta, Malta','St Julian\'s, Malta',
  'Nicosia, Cyprus','Limassol, Cyprus','Larnaca, Cyprus',

  // ── Europe — Eastern & Balkan ─────────────────────────────────────────────
  'Warsaw, Poland','Kraków, Poland','Gdańsk, Poland','Wrocław, Poland','Łódź, Poland',
  'Poznań, Poland','Szczecin, Poland','Katowice, Poland','Bydgoszcz, Poland',
  'Prague, Czech Republic','Brno, Czech Republic','Ostrava, Czech Republic','Pilsen, Czech Republic',
  'Bratislava, Slovakia','Košice, Slovakia',
  'Budapest, Hungary','Debrecen, Hungary','Miskolc, Hungary',
  'Bucharest, Romania','Cluj-Napoca, Romania','Timișoara, Romania','Iași, Romania',
  'Sofia, Bulgaria','Plovdiv, Bulgaria','Varna, Bulgaria',
  'Kyiv, Ukraine','Kharkiv, Ukraine','Lviv, Ukraine','Odessa, Ukraine','Dnipro, Ukraine',
  'Moscow, Russia','Saint Petersburg, Russia','Novosibirsk, Russia','Yekaterinburg, Russia',
  'Kazan, Russia','Nizhny Novgorod, Russia','Samara, Russia','Chelyabinsk, Russia',
  'Minsk, Belarus','Gomel, Belarus','Brest, Belarus',
  'Belgrade, Serbia','Novi Sad, Serbia','Niš, Serbia',
  'Zagreb, Croatia','Split, Croatia','Rijeka, Croatia',
  'Ljubljana, Slovenia',
  'Sarajevo, Bosnia and Herzegovina','Banja Luka, Bosnia and Herzegovina',
  'Skopje, North Macedonia',
  'Tirana, Albania','Durrës, Albania',
  'Podgorica, Montenegro',
  'Chișinău, Moldova',
  'Vilnius, Lithuania','Kaunas, Lithuania',
  'Riga, Latvia','Daugavpils, Latvia',
  'Tallinn, Estonia','Tartu, Estonia',
  'Tbilisi, Georgia','Batumi, Georgia','Kutaisi, Georgia',
  'Yerevan, Armenia','Gyumri, Armenia',
  'Baku, Azerbaijan','Ganja, Azerbaijan',

  // ── North America ────────────────────────────────────────────────────────
  'New York, USA','Los Angeles, USA','Chicago, USA','Houston, USA','Phoenix, USA',
  'Philadelphia, USA','San Antonio, USA','San Diego, USA','Dallas, USA','San Jose, USA',
  'Austin, USA','Jacksonville, USA','Fort Worth, USA','Columbus, USA','Charlotte, USA',
  'Indianapolis, USA','San Francisco, USA','Seattle, USA','Denver, USA','Oklahoma City, USA',
  'El Paso, USA','Nashville, USA','Washington DC, USA','Las Vegas, USA','Louisville, USA',
  'Portland, USA','Milwaukee, USA','Albuquerque, USA','Tucson, USA','Fresno, USA',
  'Sacramento, USA','Kansas City, USA','Mesa, USA','Atlanta, USA','Omaha, USA',
  'Colorado Springs, USA','Raleigh, USA','Long Beach, USA','Virginia Beach, USA','Minneapolis, USA',
  'Miami, USA','Tampa, USA','Orlando, USA','Boston, USA','Detroit, USA','Pittsburgh, USA',
  'Salt Lake City, USA','New Orleans, USA','Cleveland, USA','Baltimore, USA','Honolulu, USA',
  'Toronto, Canada','Montreal, Canada','Vancouver, Canada','Calgary, Canada','Edmonton, Canada',
  'Ottawa, Canada','Winnipeg, Canada','Quebec City, Canada','Hamilton, Canada','Brampton, Canada',
  'Surrey, Canada','Mississauga, Canada','Kitchener, Canada','London, Canada','Halifax, Canada',
  'Mexico City, Mexico','Guadalajara, Mexico','Monterrey, Mexico','Cancún, Mexico',
  'Puebla, Mexico','Tijuana, Mexico','León, Mexico','Ciudad Juárez, Mexico','Mérida, Mexico',
  'Havana, Cuba','Santiago de Cuba, Cuba',
  'Santo Domingo, Dominican Republic','Santiago, Dominican Republic',
  'San Juan, Puerto Rico',
  'Guatemala City, Guatemala','Quetzaltenango, Guatemala',
  'San Salvador, El Salvador',
  'Tegucigalpa, Honduras','San Pedro Sula, Honduras',
  'Managua, Nicaragua',
  'San José, Costa Rica',
  'Panama City, Panama',
  'Nassau, Bahamas',
  'Kingston, Jamaica',
  'Port-au-Prince, Haiti',
  'Port of Spain, Trinidad and Tobago',

  // ── South America ────────────────────────────────────────────────────────
  'São Paulo, Brazil','Rio de Janeiro, Brazil','Brasília, Brazil','Salvador, Brazil',
  'Fortaleza, Brazil','Belo Horizonte, Brazil','Manaus, Brazil','Curitiba, Brazil',
  'Recife, Brazil','Porto Alegre, Brazil','Belém, Brazil','Goiânia, Brazil','Florianópolis, Brazil',
  'Buenos Aires, Argentina','Córdoba, Argentina','Rosario, Argentina','Mendoza, Argentina',
  'La Plata, Argentina','San Miguel de Tucumán, Argentina','Mar del Plata, Argentina',
  'Bogotá, Colombia','Medellín, Colombia','Cali, Colombia','Barranquilla, Colombia','Cartagena, Colombia',
  'Lima, Peru','Arequipa, Peru','Trujillo, Peru','Chiclayo, Peru',
  'Santiago, Chile','Valparaíso, Chile','Concepción, Chile','Antofagasta, Chile',
  'Montevideo, Uruguay','Salto, Uruguay',
  'Caracas, Venezuela','Maracaibo, Venezuela','Valencia, Venezuela','Barquisimeto, Venezuela',
  'Quito, Ecuador','Guayaquil, Ecuador','Cuenca, Ecuador',
  'La Paz, Bolivia','Santa Cruz, Bolivia','Cochabamba, Bolivia',
  'Asunción, Paraguay','Ciudad del Este, Paraguay',
  'Georgetown, Guyana',
  'Paramaribo, Suriname',

  // ── Africa ───────────────────────────────────────────────────────────────
  'Cairo, Egypt','Alexandria, Egypt','Giza, Egypt','Shubra El Kheima, Egypt','Port Said, Egypt',
  'Lagos, Nigeria','Abuja, Nigeria','Kano, Nigeria','Ibadan, Nigeria','Port Harcourt, Nigeria',
  'Benin City, Nigeria','Kaduna, Nigeria','Enugu, Nigeria',
  'Johannesburg, South Africa','Cape Town, South Africa','Durban, South Africa','Pretoria, South Africa',
  'Port Elizabeth, South Africa','Bloemfontein, South Africa','East London, South Africa',
  'Nairobi, Kenya','Mombasa, Kenya','Kisumu, Kenya','Nakuru, Kenya',
  'Addis Ababa, Ethiopia','Dire Dawa, Ethiopia','Mekelle, Ethiopia',
  'Dar es Salaam, Tanzania','Dodoma, Tanzania','Mwanza, Tanzania','Zanzibar, Tanzania',
  'Casablanca, Morocco','Rabat, Morocco','Fez, Morocco','Marrakech, Morocco','Tangier, Morocco',
  'Accra, Ghana','Kumasi, Ghana','Tamale, Ghana',
  'Kampala, Uganda','Gulu, Uganda',
  'Kinshasa, DRC','Lubumbashi, DRC','Mbuji-Mayi, DRC',
  'Dakar, Senegal','Thiès, Senegal',
  'Abidjan, Côte d\'Ivoire','Bouaké, Côte d\'Ivoire',
  'Tunis, Tunisia','Sfax, Tunisia','Sousse, Tunisia',
  'Algiers, Algeria','Oran, Algeria','Constantine, Algeria',
  'Tripoli, Libya','Benghazi, Libya',
  'Khartoum, Sudan','Omdurman, Sudan',
  'Antananarivo, Madagascar',
  'Luanda, Angola','Huambo, Angola',
  'Lusaka, Zambia','Ndola, Zambia',
  'Harare, Zimbabwe','Bulawayo, Zimbabwe',
  'Maputo, Mozambique','Beira, Mozambique',
  'Bamako, Mali','Sikasso, Mali',
  'Conakry, Guinea',
  'Kigali, Rwanda',
  'Mogadishu, Somalia',
  'Lomé, Togo',
  'Cotonou, Benin',
  'Ouagadougou, Burkina Faso',
  'Niamey, Niger',
  'Ndjamena, Chad',
  'Yaoundé, Cameroon','Douala, Cameroon',
  'Libreville, Gabon',
  'Brazzaville, Congo','Pointe-Noire, Congo',
  'Malabo, Equatorial Guinea',
  'Freetown, Sierra Leone',
  'Monrovia, Liberia',
  'Bissau, Guinea-Bissau',
  'Banjul, Gambia',
  'Praia, Cabo Verde',
  'Windhoek, Namibia',
  'Gaborone, Botswana',
  'Maseru, Lesotho',
  'Mbabane, Eswatini',
  'Port Louis, Mauritius',
  'Moroni, Comoros',
  'Djibouti City, Djibouti',
  'Asmara, Eritrea',
  'Lilongwe, Malawi','Blantyre, Malawi',

  // ── Oceania ──────────────────────────────────────────────────────────────
  'Sydney, Australia','Melbourne, Australia','Brisbane, Australia','Perth, Australia',
  'Adelaide, Australia','Gold Coast, Australia','Canberra, Australia','Newcastle, Australia',
  'Wollongong, Australia','Logan City, Australia','Geelong, Australia','Hobart, Australia',
  'Auckland, New Zealand','Wellington, New Zealand','Christchurch, New Zealand',
  'Hamilton, New Zealand','Tauranga, New Zealand','Dunedin, New Zealand',
  'Suva, Fiji','Port Moresby, Papua New Guinea','Honiara, Solomon Islands',
  'Port Vila, Vanuatu','Apia, Samoa','Nuku\'alofa, Tonga','Funafuti, Tuvalu',
  'South Tarawa, Kiribati','Majuro, Marshall Islands','Palikir, Micronesia','Melekeok, Palau',

  // ── Countries (for broad country-level selection) ─────────────────────────
  'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda','Argentina','Armenia','Australia',
  'Austria','Azerbaijan','Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin',
  'Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso','Burundi',
  'Cabo Verde','Cambodia','Cameroon','Canada','Central African Republic','Chad','Chile','China','Colombia',
  'Comoros','Congo','Costa Rica','Croatia','Cuba','Cyprus','Czech Republic','Denmark','Djibouti','Dominica',
  'Dominican Republic','Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini',
  'Ethiopia','Fiji','Finland','France','Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada',
  'Guatemala','Guinea','Guinea-Bissau','Guyana','Haiti','Honduras','Hungary','Iceland','India','Indonesia',
  'Iran','Iraq','Ireland','Israel','Italy','Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kiribati',
  'Kuwait','Kyrgyzstan','Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania',
  'Luxembourg','Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands','Mauritania',
  'Mauritius','Mexico','Micronesia','Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique',
  'Myanmar','Namibia','Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','North Korea',
  'North Macedonia','Norway','Oman','Pakistan','Palau','Palestine','Panama','Papua New Guinea','Paraguay',
  'Peru','Philippines','Poland','Portugal','Qatar','Romania','Russia','Rwanda','Saint Kitts and Nevis',
  'Saint Lucia','Saint Vincent and the Grenadines','Samoa','San Marino','Sao Tome and Principe','Saudi Arabia',
  'Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia','Solomon Islands','Somalia',
  'South Africa','South Korea','South Sudan','Spain','Sri Lanka','Sudan','Suriname','Sweden','Switzerland',
  'Syria','Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste','Togo','Tonga','Trinidad and Tobago',
  'Tunisia','Turkey','Turkmenistan','Tuvalu','Uganda','Ukraine','United Arab Emirates','United Kingdom',
  'United States','Uruguay','Uzbekistan','Vanuatu','Vatican City','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe',
];

// ── Location autocomplete component ──────────────────────────────────────────
const LocationSearch: React.FC<{
  selected: string[];
  onChange: (locs: string[]) => void;
  max?: number;
}> = ({ selected, onChange, max = 5 }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const results = query.length >= 1
    ? LOCATION_DATA.filter(loc =>
        loc.toLowerCase().includes(query.toLowerCase()) && !selected.includes(loc)
      ).slice(0, 10)
    : [];

  const add = (loc: string) => {
    if (selected.length < max) onChange([...selected, loc]);
    setQuery('');
    setOpen(false);
  };

  const remove = (loc: string) => onChange(selected.filter(l => l !== loc));

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      {/* Selected tags */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map(loc => (
            <span key={loc}
              className="inline-flex items-center gap-1 t-meta px-2.5 py-1 rounded-full border font-medium"
              style={{ borderColor: 'rgba(108,71,255,0.3)', background: 'rgba(108,71,255,0.06)', color: 'var(--ds-text-strong)' }}>
              <MapPin className="w-3 h-3" style={{ color: 'var(--ds-brand)' }} />
              {loc}
              <button type="button" onClick={() => remove(loc)}
                className="ml-0.5 hover:text-red-400 transition-colors" style={{ color: 'var(--ds-text-muted)' }}>✕</button>
            </span>
          ))}
        </div>
      )}
      {/* Search input */}
      <div className="ds-field-control px-3 gap-2">
        <MapPin className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--ds-text-muted)' }} />
        <input
          type="text"
          value={query}
          disabled={selected.length >= max}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length >= max
            ? t('views.accountSettings.preferences.locationSearchMaxSelected', { defaultValue: 'Max {{max}} selected', max })
            : t('views.accountSettings.preferences.locationSearchPlaceholder', 'Search cities or countries…')}
          className="ds-field-input !px-0"
        />
        {selected.length > 0 && (
          <span className="t-tag t-num shrink-0" style={{ color: 'var(--ds-text-muted)' }}>
            {selected.length}/{max}
          </span>
        )}
      </div>
      {/* Dropdown results */}
      {open && results.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full rounded-xl border shadow-xl overflow-hidden"
          style={{
            borderColor: 'var(--ds-border)',
            background: 'var(--ds-bg-card)',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {results.map(loc => (
            <button
              key={loc}
              type="button"
              onMouseDown={e => { e.preventDefault(); add(loc); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left t-body transition-colors"
              style={{ color: 'var(--ds-text-strong)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--ds-bg-hover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <MapPin className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--ds-text-muted)' }} />
              {loc}
            </button>
          ))}
        </div>
      )}
      {open && query.length >= 1 && results.length === 0 && (
        <div
          className="absolute z-50 mt-1 w-full rounded-xl border px-4 py-3 t-meta"
          style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)', color: 'var(--ds-text-muted)' }}
        >
          {t('views.accountSettings.preferences.locationSearchNoMatches', { defaultValue: 'No matches for "{{query}}"', query })}
        </div>
      )}
    </div>
  );
};

// ── Preference row (label left, control right) ────────────────────────────────
const PrefRow: React.FC<{ label: string; limit?: string; children: React.ReactNode }> = ({ label, limit, children }) => (
  <div className="ds-form-row">
    <div className="ds-form-row-copy">
      <p className="t-section">{label}</p>
      {limit && <p className="t-meta mt-0.5">{limit}</p>}
    </div>
    <div className="ds-form-row-control">{children}</div>
  </div>
);

// ── Profile / preferences tab shared layout + input helpers (module-level so
//    React keeps stable component identity — inline defs inside renderContent
//    remounted the entire form on every keystroke and blocked typing). ────────
const SETTINGS_INPUT_CSS: React.CSSProperties = {
  background: 'var(--ds-bg-input)',
  border: '1px solid var(--ds-border-strong)',
  boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
  borderRadius: '8px',
  color: 'var(--ds-text-strong)',
  fontSize: 'var(--ds-fs-body)',
  fontFamily: 'var(--ds-font-sans)',
};

function settingsInputFocus(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
  e.currentTarget.style.borderColor = 'var(--ds-brand)';
  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(108,71,255,0.10)';
}

function settingsInputBlur(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
  e.currentTarget.style.borderColor = 'var(--ds-border-strong)';
  e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0,0,0,0.05)';
}

const SettingsSectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="t-section" style={{ marginBottom: '12px' }}>
    {children}
  </p>
);

const SettingsFieldLabel: React.FC<{ children: React.ReactNode; hint?: React.ReactNode }> = ({ children, hint }) => (
  <div className="ds-field-label">
    <label>{children}</label>
    {hint && <span className="t-meta t-num" style={{ color: 'var(--ds-text-disabled)' }}>{hint}</span>}
  </div>
);

const SettingsSectionDivider: React.FC = () => (
  <div className="border-t" style={{ borderColor: 'var(--ds-divider)', margin: '0' }} />
);

const SettingsFormRow: React.FC<{
  label: string;
  desc?: string;
  children: React.ReactNode;
  noDivider?: boolean;
}> = ({ label, desc, children, noDivider }) => (
  <div className={`ds-form-row ${noDivider ? 'ds-form-row-no-border' : ''}`}>
    <div className="ds-form-row-copy">
      <p className="t-section">{label}</p>
      {desc && <p className="t-meta leading-relaxed">{desc}</p>}
    </div>
    <div className="ds-form-row-control">{children}</div>
  </div>
);

/** Consistent sticky save footer used across all editable settings tabs. */
const SettingsSaveFooter: React.FC<{
  step: 1 | 2 | 3 | 4 | 5;
  isSaving: boolean;
  onSave: (step: 1 | 2 | 3 | 4 | 5) => void;
  onCancel: () => void;
  t: (key: string, fallback: string) => string;
}> = ({ step, isSaving, onSave, onCancel, t }) => (
  <div
    className="sticky bottom-0 z-10 mt-6 rounded-xl border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 px-5 py-3"
    style={{
      borderColor: 'var(--ds-border-strong)',
      background: 'var(--ds-bg-card)',
      backdropFilter: 'blur(12px)',
      boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
    }}
  >
    <p className="t-meta hidden sm:block" style={{ color: 'var(--ds-text-muted)' }}>
      {t('views.accountSettings.shared.unsavedHint', 'Make sure to save your changes.')}
    </p>
    <div className="flex items-center gap-2 w-full sm:w-auto">
      <button
        type="button"
        onClick={onCancel}
        className="ds-btn ds-btn-secondary min-h-[44px] flex-1 sm:flex-initial sm:w-auto disabled:opacity-50 touch-manipulation"
        style={{
          borderColor: 'var(--ds-border)',
          color: 'var(--ds-text-muted)',
          background: 'var(--ds-bg-card)',
        }}
      >
        {t('views.accountSettings.shared.cancel', 'Cancel')}
      </button>
      <button
        type="button"
        disabled={isSaving}
        onClick={() => void onSave(step)}
        className="ds-btn ds-btn-primary min-h-[44px] flex-1 sm:flex-initial sm:w-auto disabled:opacity-50 touch-manipulation"
      >
        {isSaving
          ? t('views.accountSettings.shared.saving', 'Saving…')
          : t('views.accountSettings.shared.saveChanges', 'Save Changes')}
      </button>
    </div>
  </div>
);

const SettingsChoicePill: React.FC<{
  label: string;
  selected: boolean;
  onClick: () => void;
}> = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`ds-choice-pill ${selected ? 'is-selected' : ''}`}
  >
    {label}
  </button>
);

const SettingsSalaryInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  currency: string;
  onCurrencyChange: (v: string) => void;
}> = ({ label, value, onChange, placeholder, currency, onCurrencyChange }) => {
  const { t } = useTranslation();
  return (
  <div>
    <label className="block t-body mb-1.5">{label}</label>
    <div className="ds-field-control">
      <div className="ds-field-prefix relative">
        <select
          value={currency}
          onChange={e => onCurrencyChange(e.target.value)}
          className="appearance-none bg-transparent pr-5 t-meta font-semibold outline-none cursor-pointer"
          style={{ minWidth: '68px', color: 'var(--ds-text-strong)' }}
        >
          <option value="INR">INR</option>
          <option value="USD">USD</option>
        </select>
        <svg
          className="pointer-events-none absolute right-2 w-3 h-3"
          style={{ color: 'var(--ds-text-muted)' }}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={e => onChange(e.target.value.replace(/[^0-9]/g, ''))}
        placeholder={placeholder}
        className="ds-field-input t-num"
      />
      <div className="ds-field-suffix t-tag font-medium whitespace-nowrap">
        {t('views.accountSettings.common.perYear', '/ yr')}
      </div>
    </div>
  </div>
  );
};

const SettingsSegmentedToggle: React.FC<{
  value: 'yes' | 'no' | null;
  onChange: (v: 'yes' | 'no') => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
  <div className="ds-segmented">
    {(['yes', 'no'] as const).map((v, i) => {
      const active = value === v;
      return (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={active ? 'is-selected' : ''}
          style={{ borderRight: i === 0 ? '1px solid var(--ds-border)' : 'none' }}
        >
          {v === 'yes' ? t('views.accountSettings.common.yes', 'Yes') : t('views.accountSettings.common.no', 'No')}
        </button>
      );
    })}
  </div>
  );
};

// ── Social link input row ─────────────────────────────────────────────────────
const SocialLinkField: React.FC<{
  icon: React.ReactNode;
  prefix: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}> = ({ icon, prefix, value, onChange, placeholder }) => (
  <div
    className="ds-field-control"
    style={{
      borderColor: value.trim() ? 'rgba(108,71,255,0.35)' : 'var(--ds-border-strong)',
    }}
  >
    {/* Locked prefix — gray background, visually separated */}
    <div
      className="ds-field-prefix gap-1.5"
    >
      <span className="shrink-0" style={{ color: 'var(--ds-text-muted)' }}>{icon}</span>
      <span className="t-tag font-medium select-none whitespace-nowrap" style={{ color: 'var(--ds-text-muted)' }}>{prefix}</span>
    </div>
    {/* Editable username part */}
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder ?? 'username'}
      className="ds-field-input min-w-0"
    />
    {value.trim() && (
      <span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mr-2.5" style={{ background: 'var(--ds-ok)' }}>
        <Check className="w-2.5 h-2.5 text-white" />
      </span>
    )}
  </div>
);


// ── TourSubTab — right-column card content (canvas provided by parent) ────────

const TourSubTab: React.FC<{ tourCompleted: boolean; onStart: () => void }> = ({ tourCompleted, onStart }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="ds-card space-y-0 !p-0 overflow-hidden">
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div
            className="relative shrink-0 flex items-center justify-center w-11 h-11 rounded-xl"
            style={{ background: tourCompleted ? 'rgba(16,185,129,0.12)' : 'rgba(99,102,241,0.10)' }}
          >
            {!tourCompleted && (
              <span
                className="absolute inset-0 rounded-xl animate-ping opacity-30"
                style={{ background: 'rgba(99,102,241,0.5)', animationDuration: '1.8s' }}
                aria-hidden
              />
            )}
            <Map
              className="w-5 h-5 relative z-10"
              style={{ color: tourCompleted ? 'var(--ds-ok)' : 'var(--ds-brand)' }}
              aria-hidden
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <h4 className="t-card-title" style={{ color: 'var(--ds-text-strong)' }}>
                {tourCompleted ? 'Tour completed' : 'Ready to explore'}
              </h4>
              {tourCompleted ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full t-tag font-semibold bg-emerald-50 text-emerald-600">
                  <CheckCircle2 className="w-3 h-3" aria-hidden />
                  Completed
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full t-tag font-semibold bg-indigo-50 text-indigo-600">
                  <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse bg-indigo-500" aria-hidden />
                  Not started
                </span>
              )}
            </div>
            <p className="t-body t-num leading-relaxed" style={{ color: 'var(--ds-text-body)' }}>
              {tourCompleted
                ? `You've completed all ${TOUR_STEPS_SUMMARY.length} steps. Replay any time to revisit a feature or show a colleague around.`
                : `A guided ${TOUR_STEPS_SUMMARY.length}-step walkthrough covering every tool — Jobs, Optimizer, AI Agent, Skill Assessment, and more.`}
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <button
                type="button"
                onClick={onStart}
                className="ds-btn ds-btn-primary touch-manipulation"
              >
                {tourCompleted
                  ? <><RotateCcw className="w-4 h-4" aria-hidden />Replay tour</>
                  : <><Play className="w-4 h-4" aria-hidden />Start tour now</>}
              </button>
              <span className="t-meta t-num">
                ~2 min · {TOUR_STEPS_SUMMARY.length} steps
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t px-6 py-4" style={{ borderColor: 'var(--ds-divider)' }}>
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1.5 t-meta t-num font-medium transition-colors touch-manipulation"
          aria-expanded={expanded}
          style={{ color: 'var(--ds-text-body)' }}
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {expanded ? 'Hide' : 'View'} all {TOUR_STEPS_SUMMARY.length} tour stops
        </button>

        {expanded && (
          <ol className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-1.5">
            {TOUR_STEPS_SUMMARY.map((label, i) => (
              <li key={label} className="flex items-center gap-2.5 t-meta" style={{ color: 'var(--ds-text-body)' }}>
                <span
                  className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center t-tag t-num font-bold"
                  style={{
                    background: tourCompleted ? 'rgba(16,185,129,0.12)' : 'rgba(99,102,241,0.10)',
                    color: tourCompleted ? 'var(--ds-ok)' : 'var(--ds-brand)',
                  }}
                >
                  {tourCompleted ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
                </span>
                {label}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};


// ── Main component ────────────────────────────────────────────────────────────
export interface AccountSettingsPageProps {
  onNavigate?: (page: DashboardPage) => void;
  initialTab?: SettingsTab;
}

const AccountSettingsPage: React.FC<AccountSettingsPageProps> = ({
  onNavigate,
  initialTab = 'profile',
}) => {
  const { t, i18n } = useTranslation();
  const { user, signOut } = useAuthentication();
  const isAdmin = user?.email === ADMIN_EMAIL;
  const authAvatarUrl =
    (user?.user_metadata as { avatar_url?: string; picture?: string } | undefined)?.avatar_url
    ?? (user?.user_metadata as { picture?: string } | undefined)?.picture
    ?? null;
  // ───────────────────────────────────────────────────────────────────────
  // Active sub-tab is mirrored into the URL as `?tab=<section>` so that
  // browser back/forward, deep links, and page refresh all work.
  //
  // Resolution priority for the *initial* tab:
  //   1. `?tab=` query string (deep link or browser back/forward)
  //   2. `initialTab` prop (from the route handler)
  //   3. 'profile' (default)
  // ───────────────────────────────────────────────────────────────────────
  const VALID_SECTIONS: readonly SettingsSection[] = [
    'profile', 'work', 'preferences', 'resume', 'security', 'notifications', 'tour',
    'api-keys', 'billing', 'payment-history',
    // admin sections (only reachable when isAdmin, but kept in the valid list
    // so ?tab= deep-links resolve correctly)
    'admin-analytics', 'control-panel', 'cron-jobs-admin', 'testimonials-admin', 'product-feedback-admin', 'translations-admin',
    'blog-cms', 'careers-cms', 'jobs-board',
  ];
  const readTabFromUrl = (): SettingsSection | null => {
    if (typeof window === 'undefined') return null;
    const t = new URLSearchParams(window.location.search).get('tab');
    return t && (VALID_SECTIONS as readonly string[]).includes(t) ? (t as SettingsSection) : null;
  };
  const readHighlightFromUrl = (): EmailNotificationType | null => {
    if (typeof window === 'undefined') return null;
    const h = new URLSearchParams(window.location.search).get('highlight');
    const valid: EmailNotificationType[] = [
      'career_tips', 'weekly_digest', 'job_digest',
      'optimization_reminder', 'onboarding_nudge', 'blog',
    ];
    return h && valid.includes(h as EmailNotificationType) ? (h as EmailNotificationType) : null;
  };
  const [notificationHighlight] = useState<EmailNotificationType | null>(() => readHighlightFromUrl());
  const [unsubError] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('unsub_error');
  });
  const [activeSection, setActiveSection] = useState<SettingsSection>(
    () => readTabFromUrl() ?? initialTab,
  );
  /** Keep Control Panel mounted after first visit so tab switches don't remount/reload. */
  const [controlPanelMounted, setControlPanelMounted] = useState(
    () => (readTabFromUrl() ?? initialTab) === 'control-panel',
  );

  useEffect(() => {
    if (activeSection === 'control-panel') {
      setControlPanelMounted(true);
    }
  }, [activeSection]);

  /**
   * Heavy, lazy-loaded admin sections are mounted once on first visit and then
   * kept alive (hidden via CSS). This prevents the Suspense fallback flash and
   * the full data refetch that made every tab switch look like a page reload.
   */
  const [visitedAdminSections, setVisitedAdminSections] = useState<Set<SettingsSection>>(
    () => new Set<SettingsSection>([readTabFromUrl() ?? initialTab]),
  );
  useEffect(() => {
    setVisitedAdminSections(prev => {
      if (prev.has(activeSection)) return prev;
      const next = new Set(prev);
      next.add(activeSection);
      return next;
    });
  }, [activeSection]);

  /** Collapsible left nav so wide admin tables (e.g. Outreach) get more room. */
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('ggj.accountNavCollapsed') === '1'; } catch { return false; }
  });
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('ggj.accountNavCollapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  /**
   * Update the active sub-tab AND mirror it into the URL. Using replaceState
   * for the initial mount and pushState for user-initiated clicks would be
   * ideal, but since `selectSection` is only called from user events we
   * always pushState (so back/forward steps through the tab history).
   */
  const selectSection = useCallback((next: SettingsSection) => {
    setActiveSection(next);
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') === next) return;        // already in URL
    params.set('tab', next);
    if (next !== 'control-panel') {
      params.delete('cpTab');
    }
    if (next !== 'cron-jobs-admin') {
      params.delete('cronView');
    }
    const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.pushState({}, '', newUrl);
  }, []);

  // On the very first render, if the URL has no `?tab=` we replace it in
  // so a refresh / share-link captures the active tab. This is a one-shot
  // effect — selectSection takes over for subsequent updates.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (readTabFromUrl()) return;
    const params = new URLSearchParams(window.location.search);
    params.set('tab', activeSection);
    const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
    // We deliberately depend on nothing — this should fire once after mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Browser back/forward: re-read the URL and sync the active tab without
  // recursing back into pushState.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => {
      const next = readTabFromUrl();
      if (next && next !== activeSection) {
        setActiveSection(next);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [activeSection]);
  const [alternateEmail, setAlternateEmail] = useState('');
  const [aiOptimizing, setAiOptimizing] = useState(false);
  const [aiOptimizeErr, setAiOptimizeErr] = useState<string | null>(null);

  // Product tour
  const { profile: tourProfile } = useProductTour({
    onNavigate: (p) => onNavigate?.(p),
  });

  // Local-only fields (not yet persisted to DB schema)
  const [pronouns, setPronouns] = useState('');
  const [calendarUrl, setCalendarUrl] = useState('');
  const [twitterHandle, setTwitterHandle] = useState('');
  const [instagramHandle, setInstagramHandle] = useState('');
  const [figmaHandle, setFigmaHandle] = useState('');
  const [productHuntHandle, setProductHuntHandle] = useState('');
  const [wellfoundHandle, setWellfoundHandle] = useState('');
  const [behanceHandle, setBehanceHandle] = useState('');
  const [youtubeHandle, setYoutubeHandle] = useState('');
  const [tiktokHandle, setTiktokHandle] = useState('');
  const [mastodonUrl, setMastodonUrl] = useState('');
  const [threadsHandle, setThreadsHandle] = useState('');
  // Preferences — extended local state
  const [preferredRoles, setPreferredRoles] = useState<string[]>([]);
  const [preferredRoleInput, setPreferredRoleInput] = useState('');
  const [locationModes, setLocationModes] = useState<string[]>([]);
  const [holdingOffers, setHoldingOffers] = useState<'yes' | 'no' | null>(null);
  const [noticePeriod, setNoticePeriod] = useState('');
  const [currentSalary, setCurrentSalary] = useState('');
  const [salaryCurrency, setSalaryCurrency] = useState('INR');
  const [companySizePrefs, setCompanySizePrefs] = useState<string[]>([]);
  const [genderIdentity, setGenderIdentity] = useState('');
  const [dob, setDob] = useState('');
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  const { profile: dbProfile, isLoading, isSaving, error, save } = useUserProfile();
  const [draft, setDraft] = useState<OnboardingDraft>({ ...EMPTY_DRAFT });
  const [hydrated, setHydrated] = useState(false);
  const [sectionErrors, setSectionErrors] = useState<Record<1 | 2 | 3 | 4 | 5, OnboardingErrors>>({ 1: {}, 2: {}, 3: {}, 4: {}, 5: {} });
  const [localError, setLocalError] = useState<string | null>(null);

  // Avatar upload state (shared with the hero preview)
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroAvatarUrl, setHeroAvatarUrl] = useState<string | null>(authAvatarUrl);
  const [heroUploadErr, setHeroUploadErr] = useState<string | null>(null);
  const heroFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setHeroAvatarUrl(authAvatarUrl); }, [authAvatarUrl]);

  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent<ProfileCompletionNavDetail>).detail;
      if (!d?.subTab) return;
      selectSection(
        d.subTab === 'links'        ? 'work'
        : d.subTab === 'preferences'  ? 'preferences'
        : d.subTab === 'resume'       ? 'resume'
        : d.subTab === 'work'         ? 'work'
        : 'profile',
      );
      if (d.sectionKey && d.subTab === 'resume') {
        window.setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent(RESUME_SECTION_FOCUS_EVENT, { detail: { sectionKey: d.sectionKey } }),
          );
        }, 400);
      }
    };
    window.addEventListener(PROFILE_COMPLETION_NAV_EVENT, h as EventListener);
    return () => window.removeEventListener(PROFILE_COMPLETION_NAV_EVENT, h as EventListener);
  }, []);

  const handleHeroFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setHeroUploadErr(t('views.accountSettings.profile.maxFileSize', 'Max 2 MB')); return; }
    setHeroUploading(true); setHeroUploadErr(null);
    try {
      const { data: { user: u } } = await supabase.auth.getUser();
      if (!u) throw new Error('Not authenticated');
      const ext = file.name.split('.').pop() ?? 'jpg';
      const path = `avatars/${u.id}.${ext}`;
      const { error: upErr } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
      if (upErr) {
        const code = (upErr as { statusCode?: string }).statusCode;
        if (code === '404' || upErr.message?.toLowerCase().includes('bucket not found')) {
          throw new Error('Avatar storage bucket not found — run the latest DB migration (20260519000000).');
        }
        throw upErr;
      }
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
      setHeroAvatarUrl(urlData.publicUrl + '?t=' + Date.now());
      await supabase.from('user_profiles').upsert({ id: u.id, avatar_url: urlData.publicUrl }, { onConflict: 'id' });
    } catch (ex) {
      setHeroUploadErr(ex instanceof Error ? ex.message : t('views.accountSettings.profile.uploadFailed', 'Upload failed'));
    } finally {
      setHeroUploading(false);
    }
  };

  const profileHydratedRef = useRef(false);

  useEffect(() => {
    if (profileHydratedRef.current || isLoading) return;
    setDraft(draftFromProfileRow(dbProfile));
    profileHydratedRef.current = true;
    setHydrated(true);
  }, [isLoading, dbProfile]);

  // Never block settings tabs indefinitely if profile fetch stalls
  useEffect(() => {
    if (profileHydratedRef.current) return;
    const t = window.setTimeout(() => {
      if (profileHydratedRef.current) return;
      setDraft(draftFromProfileRow(dbProfile));
      profileHydratedRef.current = true;
      setHydrated(true);
    }, 10_000);
    return () => window.clearTimeout(t);
  }, [dbProfile]);

  const patchDraft = useCallback((patch: Partial<OnboardingDraft>) => {
    setDraft(prev => ({ ...prev, ...patch }));
    setSectionErrors(prev => {
      const next = { ...prev };
      const touched = Object.keys(patch) as (keyof OnboardingDraft)[];
      ([1, 2, 3, 4, 5] as const).forEach(step => {
        const e = { ...next[step] };
        for (const k of touched) delete (e as Record<string, unknown>)[k];
        next[step] = e;
      });
      return next;
    });
    setLocalError(null);
  }, []);

  useEffect(() => {
    if (!dbProfile) return;
    const modes: string[] = [];
    const types = dbProfile.preferredJobTypes ?? [];
    if (types.includes('remote')) modes.push('remote');
    if (types.includes('hybrid')) modes.push('hybrid');
    if (types.includes('onsite')) modes.push('office');
    setLocationModes(modes);
    setPreferredRoles(dbProfile.preferredRoles ?? []);
    setNoticePeriod(dbProfile.noticePeriod ?? '');
    setHoldingOffers(dbProfile.holdingOffers as 'yes' | 'no' | null ?? null);
    setCurrentSalary(dbProfile.currentSalary ?? '');
    setSalaryCurrency(dbProfile.salaryCurrency ?? 'INR');
    setCompanySizePrefs(dbProfile.companySizePrefs ?? []);
    setGenderIdentity(dbProfile.genderIdentity ?? '');
    setDob(dbProfile.dateOfBirth ?? '');
    setSelectedLocations(dbProfile.preferredLocations ?? []);
  }, [dbProfile?.id, dbProfile?.preferredJobTypes]);

  const saveSection = useCallback(async (step: 1 | 2 | 3 | 4 | 5) => {
    const draftForValidation = step === 3 ? mergeLocationModesIntoDraft(draft, locationModes) : draft;
    const errs = validateOnboardingStep(step, draftForValidation, t);
    setSectionErrors(prev => ({ ...prev, [step]: errs }));
    if (Object.keys(errs).length > 0) return;
    setLocalError(null);
    try {
      let patch = partialUserProfileFromDraftStep(step, draftForValidation);
      if (step === 1) {
        // Work tab: merge skills (step 2) + links (step 4) into a single save
        Object.assign(patch, partialUserProfileFromDraftStep(2, draft), partialUserProfileFromDraftStep(4, draft));
      }
      if (step === 3) {
        Object.assign(patch, {
          preferredRoles:     preferredRoles,
          noticePeriod:       noticePeriod || null,
          holdingOffers:      holdingOffers,
          currentSalary:      currentSalary || null,
          salaryCurrency:     salaryCurrency || 'INR',
          companySizePrefs:   companySizePrefs,
          genderIdentity:     genderIdentity || null,
          dateOfBirth:        dob || null,
          preferredLocations: selectedLocations,
        });
      }
      await save(patch);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : t('profile.saveFailed', 'Save failed.'));
    }
  }, [draft, save, locationModes, t, preferredRoles, noticePeriod, holdingOffers, currentSalary, salaryCurrency, companySizePrefs, genderIdentity, dob, selectedLocations]);

  const cancelChanges = useCallback(() => {
    setDraft(draftFromProfileRow(dbProfile));
    setSectionErrors({ 1: {}, 2: {}, 3: {}, 4: {}, 5: {} });
    setLocalError(null);
  }, [dbProfile]);

  const optimizeBio = useCallback(async () => {
    if (!draft.headline.trim()) return;
    setAiOptimizing(true); setAiOptimizeErr(null);
    try {
      const { data, error } = await supabase.functions.invoke('ai-companion', {
        body: {
          message: `Rewrite the following professional bio to be more compelling and concise (max 120 characters). Return ONLY the improved bio text with no explanation, no quotes, and no extra formatting.\n\nBio: "${draft.headline}"`,
        },
      });
      if (error) throw error;
      const improved: string = data?.reply ?? data?.content ?? data?.message ?? data?.text ?? '';
      if (improved) patchDraft({ headline: improved.slice(0, 120) });
    } catch {
      setAiOptimizeErr(t('views.accountSettings.profile.aiOptimizeFailed', 'AI optimization failed. Try again.'));
    } finally {
      setAiOptimizing(false);
    }
  }, [draft.headline, patchDraft, t]);

  const visibleNav = NAV_ITEMS.filter(n => !n.adminOnly || isAdmin);

  // ── All settings content (flat — all sections at same level) ────────────────
  const renderContent = () => {
    const profileLoading = !hydrated && isLoading;

    // Helper: split displayName into first / last
    const nameParts = (draft.displayName || '').split(' ');
    const firstName = nameParts[0] ?? '';
    const lastName  = nameParts.slice(1).join(' ');

    // Shared gradient banner for form-based sections
    const formBanner = (section: SettingsSection) => {
      const { title, desc } = SECTION_META[section];
      const resolvedTitle = section === 'profile'
        ? t('views.accountSettings.profile.title', 'Profile')
        : section === 'work'
          ? t('views.accountSettings.work.title', 'Work')
          : section === 'preferences'
            ? t('views.accountSettings.preferences.title', 'Preferences')
            : section === 'resume'
              ? t('views.accountSettings.resume.title', 'Resume')
              : title;
      const resolvedDesc = section === 'profile'
        ? t('views.accountSettings.profile.desc', 'Update your photo and personal details.')
        : section === 'work'
          ? t('views.accountSettings.work.desc', 'Your job title, experience, skills and social links.')
          : section === 'preferences'
            ? t('views.accountSettings.preferences.desc', 'Set your job search status, target roles and preferences.')
            : section === 'resume'
              ? t('views.accountSettings.resume.desc', 'Your work history, education and skills.')
              : desc;
      return (
        <ProfileBanner
          avatarUrl={heroAvatarUrl}
          userEmail={user?.email ?? ''}
          onPhotoClick={() => heroFileRef.current?.click()}
          uploading={heroUploading}
          fileRef={heroFileRef}
          onFileChange={handleHeroFile}
          uploadErr={heroUploadErr}
          title={resolvedTitle}
          description={resolvedDesc}
          isSaving={isSaving}
        />
      );
    };

    // Error toast
    const errorToast = (error ?? localError) ? (
      <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 t-body text-red-400">
        {localError ?? error}
      </div>
    ) : null;

    // Loading state for profile-type sections
    if (profileLoading && ['profile','work','preferences','resume'].includes(activeSection)) {
      return (
        <div className="flex items-center gap-2 py-12" style={{ color: 'var(--ds-text-muted)' }}>
          <LoadingSpinner size="sm" showText={false} /> {t('views.accountSettings.profile.loading', 'Loading profile…')}
        </div>
      );
    }

    // ── Shared polished input CSS (used in profile tab inline inputs) ──────────
    const inputCss = SETTINGS_INPUT_CSS;
    const inputFocus = settingsInputFocus;
    const inputBlur = settingsInputBlur;
    const SectionLabel = SettingsSectionLabel;
    const FieldLabel = SettingsFieldLabel;
    const SectionDivider = SettingsSectionDivider;

    switch (activeSection) {
      // ── Profile: identity, emails, bio — with timeline checklist panel ────────
      case 'profile': {
        const dateLocaleMap: Record<string, string> = { en: 'en-US', es: 'es-ES', pt: 'pt-BR', ru: 'ru-RU' };
        const localeForDate = dateLocaleMap[i18n.language?.split('-')[0] || ''] ?? i18n.language ?? 'en-US';

        // Joining date from auth
        const joinedDate = user?.created_at
          ? new Intl.DateTimeFormat(localeForDate, { month: 'long', year: 'numeric' }).format(new Date(user.created_at))
          : null;

        // Dynamic timeline checklist — icons + vertical connectors
        type ClItem = { label: string; hint?: string; done: boolean; action?: () => void; Icon: React.ComponentType<{ className?: string }> };
        const clItems: ClItem[] = [
          { label: t('views.accountSettings.profile.checklist.addPhoto', 'Add a profile photo'),       hint: t('views.accountSettings.profile.checklist.addPhotoHint', 'Profiles with photos get 3× more views.'),            done: !!heroAvatarUrl,          Icon: Camera    },
          { label: t('views.accountSettings.profile.checklist.addBio', 'Add a detailed biography'),  hint: t('views.accountSettings.profile.checklist.addBioHint', 'Suggestion: Add more detail to improve match rate.'), done: draft.headline.length >= 50, Icon: FileText  },
          { label: t('views.accountSettings.profile.checklist.addLocation', 'Add your location'),         hint: t('views.accountSettings.profile.checklist.addLocationHint', 'Helps match you with nearby opportunities.'),         done: !!draft.location,         Icon: MapPin    },
          { label: t('views.accountSettings.profile.checklist.confirmEmail', 'Confirm alternate email'),   hint: t('views.accountSettings.profile.checklist.confirmEmailHint', 'For job alert notifications.'),                       done: !!alternateEmail.trim(),  Icon: Mail      },
          { label: t('views.accountSettings.profile.checklist.completeWork', 'Complete work details'),     hint: t('views.accountSettings.profile.checklist.completeWorkHint', 'Add job title and experience.'),                      done: !!draft.currentRole,      Icon: Briefcase, action: () => selectSection('work') },
        ];
        const doneCount = clItems.filter(i => i.done).length;
        const pct = Math.round((doneCount / clItems.length) * 100);

        return (
          <div key={i18n.language}>
            {errorToast}
            {formBanner('profile')}

            {/* Two-column layout */}
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 mt-5">

              {/* ── Left: form ─────────────────────────────────────────────── */}
              <div className="flex-1 min-w-0 space-y-0">

                {/* Meta chips: joined date + location */}
                {(joinedDate || draft.location) && (
                  <div className="flex flex-wrap items-center gap-5 pb-5 t-body" style={{ color: 'var(--ds-text-disabled)' }}>
                    {joinedDate && (
                      <span className="flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5" /> {t('views.accountSettings.profile.joined', { defaultValue: 'Joined {{date}}', date: joinedDate })}
                      </span>
                    )}
                    {draft.location && (
                      <span className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" style={{ color: 'var(--ds-brand)' }} /> {draft.location}
                      </span>
                    )}
                  </div>
                )}

                {/* ── Identity ─────────────────────────────────────────────── */}
                <div className="py-4">
                  <SectionLabel>{t('views.accountSettings.profile.identity', 'Identity')}</SectionLabel>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <FieldLabel hint={sectionErrors[1].displayName ? <span className="text-red-500">{sectionErrors[1].displayName}</span> : undefined}>
                        {t('views.accountSettings.profile.firstName', 'First name')} <span style={{ color: 'var(--ds-danger)' }}>*</span>
                      </FieldLabel>
                      <input
                        type="text"
                        value={firstName}
                        onChange={e => patchDraft({ displayName: [e.target.value, lastName].filter(Boolean).join(' ') })}
                        placeholder={t('views.accountSettings.profile.firstNamePlaceholder', 'Dharmendra')}
                        className="w-full px-3 py-2 outline-none transition-all"
                        style={inputCss}
                        onFocus={inputFocus} onBlur={inputBlur}
                      />
                    </div>
                    <div>
                      <FieldLabel>{t('views.accountSettings.profile.lastName', 'Last name')}</FieldLabel>
                      <input
                        type="text"
                        value={lastName}
                        onChange={e => patchDraft({ displayName: [firstName, e.target.value].filter(Boolean).join(' ') })}
                        placeholder={t('views.accountSettings.profile.lastNamePlaceholder', 'Singh')}
                        className="w-full px-3 py-2 outline-none transition-all"
                        style={inputCss}
                        onFocus={inputFocus} onBlur={inputBlur}
                      />
                    </div>
                  </div>
                </div>

                <SectionDivider />

                {/* ── Communications ───────────────────────────────────────── */}
                <div className="py-4">
                  <SectionLabel>{t('views.accountSettings.profile.communications', 'Communications')}</SectionLabel>
                  <div className="space-y-3">
                    {/* Primary email — readonly with single VERIFIED badge inside field */}
                    <div>
                      <FieldLabel>{t('views.accountSettings.profile.primaryEmail', 'Primary email')}</FieldLabel>
                      <div
                        className="w-full px-3 py-2 flex items-center justify-between"
                        style={{ ...inputCss, borderColor: 'rgba(16,185,129,0.4)', background: 'rgba(16,185,129,0.04)', cursor: 'default' }}
                      >
                        <span>{user?.email ?? ''}</span>
                        <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full t-tag" style={{ fontWeight: 700, color: 'var(--ds-ok)', background: 'rgba(16,185,129,0.12)', letterSpacing: '0.05em' }}>
                          <CheckCircle2 className="w-3 h-3" /> {t('views.accountSettings.profile.verified', 'VERIFIED')}
                        </span>
                      </div>
                      <p className="t-tag" style={{ color: 'var(--ds-text-disabled)', marginTop: '3px' }}>{t('views.accountSettings.profile.primaryEmailHint', 'Login email. Contact support to change.')}</p>
                    </div>

                    {/* Alternate email */}
                    <div>
                      <FieldLabel hint={t('views.accountSettings.profile.alternateEmailHint', 'OPTIONAL · For job alerts')}>{t('views.accountSettings.profile.alternateEmail', 'Alternate contact email')}</FieldLabel>
                      <input
                        type="email"
                        value={alternateEmail}
                        onChange={e => setAlternateEmail(e.target.value)}
                        placeholder={t('views.accountSettings.profile.alternateEmailPlaceholder', 'backup@example.com')}
                        className="w-full px-3 py-2 outline-none transition-all"
                        style={inputCss}
                        onFocus={inputFocus} onBlur={inputBlur}
                      />
                    </div>
                  </div>
                </div>

                <SectionDivider />

                {/* ── Professional Bio ──────────────────────────────────────── */}
                <div className="py-4">
                  <SectionLabel>{t('views.accountSettings.profile.professionalBio', 'Professional Bio')}</SectionLabel>
                  <div className="space-y-3">
                    {/* Brief bio with char count + AI button */}
                    <div>
                      <FieldLabel hint={<span style={{ color: draft.headline.length > 100 ? 'var(--ds-ok)' : 'var(--ds-text-disabled)' }}>{draft.headline.length} / 120</span>}>
                        {t('views.accountSettings.profile.briefBio', 'Brief bio')} <span style={{ color: 'var(--ds-danger)' }}>*</span>
                      </FieldLabel>
                      <textarea
                        value={draft.headline}
                        onChange={e => { if (e.target.value.length <= 120) patchDraft({ headline: e.target.value }); }}
                        placeholder={t('views.accountSettings.profile.briefBioPlaceholder', 'e.g. Product Manager · 6 yrs · Bengaluru')}
                        rows={2}
                        className="w-full px-3 py-2 outline-none transition-all resize-none"
                        style={inputCss}
                        onFocus={inputFocus} onBlur={inputBlur}
                      />
                      <div className="flex items-center justify-between mt-1.5">
                        <p className="t-tag" style={{ color: 'var(--ds-text-disabled)' }}>{t('views.accountSettings.profile.briefBioHint', 'First thing peers read after your name.')}</p>
                        <button
                          type="button"
                          disabled={aiOptimizing || !draft.headline.trim()}
                          onClick={() => void optimizeBio()}
                          className="ds-btn ds-btn-sm ds-btn-secondary disabled:opacity-50 shrink-0 ml-3"
                          style={{ background: 'rgba(108,71,255,0.10)', color: 'var(--ds-brand)', border: '1px solid rgba(108,71,255,0.2)' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(108,71,255,0.18)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'rgba(108,71,255,0.10)')}
                        >
                          {aiOptimizing ? <LoadingSpinner size="xs" showText={false} /> : <Sparkles className="w-3 h-3" />}
                          {t('views.accountSettings.profile.aiOptimize', 'AI-Optimize')}
                        </button>
                      </div>
                      {aiOptimizeErr && <p className="t-tag" style={{ color: 'var(--ds-danger)', marginTop: '3px' }}>{aiOptimizeErr}</p>}
                    </div>

                    {/* Location (MapPin label) + Pronouns */}
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <FieldLabel>
                          <span className="flex items-center gap-1.5">
                            <MapPin className="w-3 h-3" style={{ color: 'var(--ds-brand)' }} /> {t('views.accountSettings.profile.location', 'Location')}
                          </span>
                        </FieldLabel>
                        <input
                          type="text"
                          value={draft.location}
                          onChange={e => patchDraft({ location: e.target.value })}
                          placeholder={t('views.accountSettings.profile.locationPlaceholder', 'Bengaluru, India')}
                          className="w-full px-3 py-2 outline-none transition-all"
                          style={inputCss}
                          onFocus={inputFocus} onBlur={inputBlur}
                        />
                      </div>
                      <div>
                        <FieldLabel>{t('views.accountSettings.profile.pronouns', 'Personal pronouns')}</FieldLabel>
                        <select
                          value={pronouns}
                          onChange={e => setPronouns(e.target.value)}
                          className="w-full px-3 py-2 outline-none transition-all appearance-none"
                          style={inputCss}
                          onFocus={inputFocus} onBlur={inputBlur}
                        >
                          <option value="">{t('views.accountSettings.profile.pronounPreferNot', 'Prefer not to say')}</option>
                          <option value="he/him">{t('views.accountSettings.profile.pronounHeHim', 'he/him')}</option>
                          <option value="she/her">{t('views.accountSettings.profile.pronounSheHer', 'she/her')}</option>
                          <option value="they/them">{t('views.accountSettings.profile.pronounTheyThem', 'they/them')}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <SettingsSaveFooter step={1} isSaving={isSaving} onSave={saveSection} onCancel={cancelChanges} t={t} />
              </div>

              {/* ── Right: Timeline Checklist ────────────────────────────────── */}
              <div className="w-full lg:w-[240px] shrink-0 order-first lg:order-none">
                <div className="rounded-xl border lg:sticky lg:top-4 overflow-hidden" style={{ borderColor: 'var(--ds-border)' }}>
                  {/* Card header */}
                  <div className="px-4 pt-4 pb-3">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="t-label" style={{ color: 'var(--ds-text-body)' }}>
                        {t('views.accountSettings.profile.completenessTitle', 'Profile Completeness')}
                      </h3>
                      <span
                        className="px-2 py-0.5 rounded-full t-tag t-num font-bold"
                        style={{
                          background: pct >= 100 ? 'rgba(16,185,129,0.12)' : 'rgba(108,71,255,0.10)',
                          color: pct >= 100 ? 'var(--ds-ok)' : 'var(--ds-brand)',
                        }}
                      >
                        {pct}%
                      </span>
                    </div>
                    {/* Progress bar — matches dashboard ATS readiness widget */}
                    <div className="w-full rounded-full overflow-hidden" style={{ height: '6px', background: 'var(--ds-divider)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${pct}%`,
                          background: pct >= 100
                            ? 'linear-gradient(90deg,var(--ds-ok),var(--ds-ok))'
                            : 'linear-gradient(90deg,var(--ds-brand),var(--ds-brand-hover))',
                        }}
                      />
                    </div>
                    <p className="t-tag t-num" style={{ color: 'var(--ds-text-disabled)', marginTop: '5px' }}>
                      {t('views.accountSettings.profile.stepsComplete', {
                        defaultValue: '{{done}} of {{total}} steps complete',
                        done: doneCount,
                        total: clItems.length,
                      })}
                    </p>
                  </div>

                  <div className="border-t" style={{ borderColor: 'var(--ds-divider)' }} />

                  {/* Timeline list */}
                  <div className="px-4 py-3">
                    {clItems.map((item, i) => {
                      const isLast = i === clItems.length - 1;
                      const { Icon } = item;
                      return (
                        <div key={i} className="flex gap-2.5">
                          {/* Track: icon badge + vertical connector */}
                          <div className="flex flex-col items-center shrink-0" style={{ width: '24px' }}>
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                              style={{
                                background: item.done ? 'rgba(16,185,129,0.12)' : 'rgba(108,71,255,0.07)',
                                border: `1.5px solid ${item.done ? 'var(--ds-ok)' : 'rgba(108,71,255,0.25)'}`,
                              }}
                            >
                              {item.done
                                ? <Check className="w-3 h-3" style={{ color: 'var(--ds-ok)', strokeWidth: 2.5 }} />
                                : <Icon className="w-3 h-3" style={{ color: 'var(--ds-brand)' }} />}
                            </div>
                            {!isLast && (
                              <div className="flex-1 w-px mt-1 mb-1" style={{ background: 'var(--ds-border)', minHeight: '10px' }} />
                            )}
                          </div>

                          {/* Content — done items: muted gray, no strikethrough */}
                          <div className={`flex-1 min-w-0 ${isLast ? 'pb-0' : 'pb-3'}`}>
                            <p className={item.done ? 't-meta' : 't-body'} style={{
                              fontWeight: item.done ? 400 : 500,
                              color: item.done ? 'var(--ds-text-disabled)' : 'var(--ds-text-strong)',
                            }}>
                              {item.label}
                            </p>
                            {!item.done && item.hint && (
                              <p className="t-tag" style={{ color: 'var(--ds-text-disabled)', lineHeight: '1.35', marginTop: '1px' }}>{item.hint}</p>
                            )}
                            {!item.done && item.action && (
                              <button
                                type="button"
                                onClick={item.action}
                                style={{ fontWeight: 600, color: 'var(--ds-brand)' }}
                                className="t-tag hover:underline"
                              >
                                {t('views.accountSettings.profile.goToWork', 'Go to Work →')}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {pct >= 100 && (
                    <div className="mx-4 mb-4 rounded-lg px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.18)' }}>
                      <Star className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--ds-ok)' }} />
                      <p className="t-tag" style={{ fontWeight: 600, color: 'var(--ds-ok)' }}>{t('views.accountSettings.profile.profileComplete', 'Profile complete!')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      // ── Work: job title, experience, skills, social links ───────────────────
      case 'work': {
        const workTagCount = draft.skills?.length ?? 0;
        const workSkillsOver = workTagCount > PROFILE_SKILLS_MAX;
        const skillsOverCount = workTagCount - PROFILE_SKILLS_MAX;
        const WorkRow = SettingsFormRow;

        return (
          <div key={i18n.language}>
            {errorToast}
            {formBanner('work')}
            <div className="w-full pb-10 mt-4">

              {/* ── Role & experience ──────────────────────────────────────── */}
              <WorkRow
                label={t('views.accountSettings.work.roleExperience', 'Role & experience')}
                desc={t('views.accountSettings.work.roleExperienceDesc', "Your current position and how long you've been working.")}
              >
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field
                    label={t('views.accountSettings.work.currentJobTitle', 'Current job title')} required
                    value={draft.currentRole}
                    onChange={v => patchDraft({ currentRole: v })}
                    placeholder={t('views.accountSettings.work.currentJobTitlePlaceholder', 'e.g. Senior Product Manager')}
                    error={sectionErrors[1].currentRole ?? null}
                  />
                  <Field
                    label={t('views.accountSettings.work.yearsExperience', 'Years of experience')} required
                    type="number"
                    value={draft.experienceYears ?? ''}
                    onChange={v => patchDraft({ experienceYears: v as unknown as string })}
                    placeholder={t('views.accountSettings.work.yearsExperiencePlaceholder', 'e.g. 6')}
                    error={sectionErrors[1].experienceYears ?? null}
                  />
                </div>
              </WorkRow>

              {/* ── Online presence ────────────────────────────────────────── */}
              <WorkRow
                label={t('views.accountSettings.work.onlinePresence', 'Online presence')}
                desc={t('views.accountSettings.work.onlinePresenceDesc', 'Your portfolio site and scheduling link.')}
              >
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field
                    label={t('views.accountSettings.work.website', 'Website')}
                    type="url"
                    value={draft.portfolioUrl}
                    onChange={v => patchDraft({ portfolioUrl: v })}
                    placeholder={t('views.accountSettings.work.websitePlaceholder', 'https://your-site.com')}
                  />
                  <Field
                    label={t('views.accountSettings.work.calendarLink', 'Calendar link')}
                    type="url"
                    value={calendarUrl}
                    onChange={setCalendarUrl}
                    placeholder={t('views.accountSettings.work.calendarLinkPlaceholder', 'https://cal.com/...')}
                    hint={t('views.accountSettings.work.calendarLinkHint', 'Cal.com or Calendly URL')}
                  />
                </div>
              </WorkRow>

              {/* ── Profile Tags ───────────────────────────────────────────── */}
              <WorkRow
                label={t('views.accountSettings.work.skillsTagsLabel', {
                  defaultValue: 'Skills & tags ({{count}}/{{max}})',
                  count: workTagCount,
                  max: PROFILE_SKILLS_MAX,
                })}
                desc={
                  workSkillsOver
                    ? t('views.accountSettings.work.skillsDescOver', {
                        count: skillsOverCount,
                        defaultValue_one: 'Remove {{count}} skill to stay within the limit. These power job matching and recommendations.',
                        defaultValue_other: 'Remove {{count}} skills to stay within the limit. These power job matching and recommendations.',
                      })
                    : t('views.accountSettings.work.skillsDescDefault', {
                        defaultValue: 'Add up to {{max}} skills, tools, or roles that best represent your work.',
                        max: PROFILE_SKILLS_MAX,
                      })
                }
              >
                <div
                  className="rounded-lg border overflow-hidden"
                  style={{ borderColor: 'var(--ds-border-strong)', background: 'var(--ds-bg-card)', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.04)' }}
                >
                  <ProfileSkillsFields idPrefix="prof-work" draft={draft} errors={sectionErrors[2]} onPatch={patchDraft} />
                </div>
              </WorkRow>

              {/* ── Social profiles ────────────────────────────────────────── */}
              <WorkRow
                label={t('views.accountSettings.work.socialProfiles', 'Social profiles')}
                desc={t('views.accountSettings.work.socialProfilesDesc', 'Add your username — the platform domain is already prefixed.')}
                noDivider
              >
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {/* Twitter/X */}
                  <SocialLinkField
                    icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>}
                    prefix="twitter.com/" value={twitterHandle} onChange={setTwitterHandle}
                  />
                  {/* LinkedIn */}
                  <SocialLinkField
                    icon={<Linkedin className="w-3.5 h-3.5" style={{ color: 'var(--ds-info)' }} />}
                    prefix="linkedin.com/in/"
                    value={extractLinkedInHandle(draft.linkedinUrl)}
                    onChange={h => patchDraft({ linkedinUrl: h ? `https://linkedin.com/in/${h}` : '' })}
                  />
                  {/* Instagram */}
                  <SocialLinkField
                    icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="url(#ig)" strokeWidth="1.8"><defs><linearGradient id="ig" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="var(--ds-warn)"/><stop offset="25%" stopColor="var(--ds-danger)"/><stop offset="50%" stopColor="var(--ds-danger)"/><stop offset="75%" stopColor="var(--ds-danger)"/><stop offset="100%" stopColor="var(--ds-brand)"/></linearGradient></defs><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1" fill="var(--ds-danger)" stroke="none"/></svg>}
                    prefix="instagram.com/" value={instagramHandle} onChange={setInstagramHandle}
                  />
                  {/* Figma */}
                  <SocialLinkField
                    icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"><path d="M8 2h8a4 4 0 1 1 0 8H8z" fill="var(--ds-warn)"/><path d="M8 10h4a4 4 0 0 1 0 8H8z" fill="var(--ds-brand)"/><path d="M8 18a4 4 0 0 0 4 4V18z" fill="var(--ds-ok)"/><path d="M8 2H4a4 4 0 0 0 0 8h4z" fill="var(--ds-danger)"/><circle cx="16" cy="12" r="4" fill="var(--ds-info)"/></svg>}
                    prefix="figma.com/@" value={figmaHandle} onChange={setFigmaHandle}
                  />
                  {/* Product Hunt */}
                  <SocialLinkField
                    icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="var(--ds-warn)"><path d="M13.604 8.4h-3.405V12h3.405a1.8 1.8 0 1 0 0-3.6zM12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm1.604 14.4H10.2V18H7.8V6h5.804a4.2 4.2 0 1 1 0 8.4z"/></svg>}
                    prefix="producthunt.com/@" value={productHuntHandle} onChange={setProductHuntHandle}
                  />
                  {/* Wellfound */}
                  <SocialLinkField
                    icon={<span className="t-tag font-bold" style={{ color: 'var(--ds-text-strong)', fontFamily: 'serif' }}>W</span>}
                    prefix="wellfound.com/u/" value={wellfoundHandle} onChange={setWellfoundHandle}
                  />
                  {/* Behance */}
                  <SocialLinkField
                    icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="var(--ds-info)"><path d="M6.938 4.503c.702 0 1.34.06 1.92.188.577.13 1.07.33 1.485.61.41.28.733.65.96 1.12.225.47.34 1.05.34 1.73 0 .74-.17 1.36-.507 1.86-.338.5-.837.9-1.502 1.22.906.26 1.576.72 2.022 1.37.448.66.67 1.45.67 2.36 0 .75-.13 1.39-.41 1.93-.28.55-.67 1-1.16 1.35-.49.35-1.06.61-1.7.78-.64.17-1.32.25-2.03.25H0V4.51h6.938zM16.955 1.69H23v1.49h-6.045V1.69zm-2.3 10.2c.195-.28.29-.64.29-1.09 0-.44-.08-.81-.25-1.09-.17-.28-.4-.5-.68-.64-.28-.14-.6-.23-.95-.27-.35-.04-.71-.06-1.08-.06H3.56v4.23h8.43c.43 0 .83-.04 1.19-.12.37-.08.69-.21.97-.4zm-9.05 4.97H3.56v4.98h2.18c.38 0 .76-.03 1.11-.1.35-.06.67-.17.94-.34.27-.17.48-.4.63-.7.16-.3.23-.68.23-1.14 0-.91-.26-1.56-.77-1.95-.52-.39-1.18-.75-2.1-.75h-.03zm11.22-1.13h-5.73V17h5.73v-1.27zm3.46-2.19H16.4V10h3.88v2.54zm1.81-4.54c-.41-.95-1.01-1.77-1.79-2.45-.78-.68-1.7-1.2-2.74-1.57-1.04-.37-2.17-.55-3.37-.55-1.26 0-2.42.2-3.47.6-1.05.4-1.95.97-2.7 1.7-.75.73-1.33 1.62-1.74 2.66-.41 1.04-.62 2.22-.62 3.52 0 1.36.2 2.57.61 3.63.4 1.06.98 1.96 1.72 2.69.74.73 1.63 1.28 2.67 1.66 1.04.38 2.2.57 3.48.57 2.01 0 3.74-.5 5.16-1.5 1.43-1 2.38-2.56 2.85-4.67h-3.6c-.14.55-.49 1.07-1.04 1.55-.55.48-1.35.72-2.39.72-.93 0-1.71-.21-2.35-.64-.63-.43-1.02-1.2-1.16-2.31h10.64c.03-.22.04-.42.04-.6v-.56c0-1.28-.19-2.41-.59-3.4l.01.01z"/></svg>}
                    prefix="behance.net/" value={behanceHandle} onChange={setBehanceHandle}
                  />
                  {/* Mastodon */}
                  <SocialLinkField
                    icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="var(--ds-info)"><path d="M23.268 5.313c-.35-2.408-2.647-4.286-5.44-4.697C17.097.45 13.927.1 12.04.1H12c-1.886 0-5.06.35-5.79.516-2.79.41-5.09 2.288-5.44 4.697C.618 7.29.5 8.877.5 11.61c0 3.68.205 6.44 1.27 7.46 1.01.972 2.44 1.375 3.985 1.484.067.004 2.247.15 4.72.15 2.478 0 4.662-.15 4.72-.154 1.544-.115 2.974-.51 3.985-1.48 1.062-1.02 1.27-3.78 1.27-7.46 0-2.732-.117-4.32-.182-6.297zm-6.45 8.625H14.87v-5.63c0-1.198-.503-1.805-1.513-1.805-1.115 0-1.675.72-1.675 2.143v3.1H9.737v-3.1c0-1.423-.56-2.143-1.675-2.143-1.01 0-1.513.607-1.513 1.805v5.63H4.605v-5.8c0-1.197.305-2.147.913-2.85.627-.7 1.45-1.058 2.476-1.058 1.182 0 2.078.454 2.67 1.363l.576.966.575-.966c.592-.91 1.488-1.363 2.67-1.363 1.025 0 1.848.357 2.476 1.058.608.703.913 1.653.913 2.85v5.8z"/></svg>}
                    prefix="mastodon" value={mastodonUrl} onChange={setMastodonUrl}
                    placeholder={t('views.accountSettings.work.mastodonPlaceholder', '@you@mastodon.social')}
                  />
                  {/* TikTok */}
                  <SocialLinkField
                    icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.32 6.32 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.53V6.77a4.85 4.85 0 0 1-1.01-.08z"/></svg>}
                    prefix="tiktok.com/@" value={tiktokHandle} onChange={setTiktokHandle}
                  />
                  {/* YouTube */}
                  <SocialLinkField
                    icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="var(--ds-danger)"><path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 00.5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 002.1 2.1C4.5 20.4 12 20.4 12 20.4s7.5 0 9.4-.5a3 3 0 002.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.6 15.6V8.4l6.4 3.6-6.4 3.6z"/></svg>}
                    prefix="youtube.com/" value={youtubeHandle} onChange={setYoutubeHandle}
                  />
                  {/* Threads */}
                  <SocialLinkField
                    icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.474 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.018 5.143.844 6.895 2.526 1.28 1.227 2.057 2.86 2.312 4.858l-2.361.448c-.144-1.221-.49-2.509-1.411-3.385-1.13-1.08-2.956-1.629-5.436-1.647-2.844.02-5.049.856-6.553 2.486-1.515 1.641-2.284 4.047-2.307 7.15.024 3.103.793 5.51 2.307 7.15 1.504 1.63 3.71 2.467 6.553 2.487 2.318-.019 4.252-.638 5.582-1.79.854-.744 1.29-1.72 1.432-3.02h-6.944v-2.3h9.347v1.147c0 3.57-.958 6.354-2.85 8.272-1.91 1.936-4.567 2.924-7.899 2.92z"/></svg>}
                    prefix="threads.net/@" value={threadsHandle} onChange={setThreadsHandle}
                  />
                </div>
              </WorkRow>

              <SettingsSaveFooter step={1} isSaving={isSaving} onSave={saveSection} onCancel={cancelChanges} t={t} />
            </div>
          </div>
        );
      }

      // ── Preferences: job search status, roles, location modes ────────────────
      case 'preferences': {
        const jss = draft.jobSearchStatus;
        const toggleLocationMode = (mode: string) =>
          setLocationModes(prev => prev.includes(mode) ? prev.filter(m => m !== mode) : [...prev, mode]);
        const toggleCompanySize = (size: string) =>
          setCompanySizePrefs(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]);
        const addRole = () => {
          const r = preferredRoleInput.trim();
          if (r && preferredRoles.length < 5 && !preferredRoles.includes(r)) {
            setPreferredRoles(prev => [...prev, r]);
            setPreferredRoleInput('');
          }
        };
        const PrefWorkRow = SettingsFormRow;
        const Pill = SettingsChoicePill;
        const SegmentedToggle = SettingsSegmentedToggle;
        const availabilityOptions = [
          {
            val: 'actively_looking' as const,
            title: t('views.accountSettings.preferences.jobSearchStatus.actively_looking.title', 'Actively applying'),
            desc: t('views.accountSettings.preferences.jobSearchStatus.actively_looking.desc', 'Get recommended to recruiters hiring for relevant roles.'),
          },
          {
            val: 'open' as const,
            title: t('views.accountSettings.preferences.jobSearchStatus.open.title', 'Casually looking'),
            desc: t('views.accountSettings.preferences.jobSearchStatus.open.desc', "Recruiters can discover you, and we'll selectively recommend new jobs."),
          },
          {
            val: 'not_looking' as const,
            title: t('views.accountSettings.preferences.jobSearchStatus.not_looking.title', 'Not looking'),
            desc: t('views.accountSettings.preferences.jobSearchStatus.not_looking.desc', "Your profile won't be recommended to recruiters."),
          },
        ];
        const jobTypeOptions = [
          { v: 'full_time', l: t('profile.options.jobType.full_time', 'Full-time') },
          { v: 'part_time', l: t('profile.options.jobType.part_time', 'Part-time') },
          { v: 'contract', l: t('profile.options.jobType.contract', 'Contract') },
          { v: 'internship', l: t('profile.options.jobType.internship', 'Internship') },
          { v: 'remote', l: t('profile.options.jobType.remote', 'Remote') },
          { v: 'hybrid', l: t('profile.options.jobType.hybrid', 'Hybrid') },
          { v: 'onsite', l: t('profile.options.jobType.onsite', 'On-site') },
        ];
        const locationModeOptions = [
          { v: 'remote', l: t('views.accountSettings.preferences.locationRemoteOnly', 'Remote only') },
          { v: 'office', l: t('views.accountSettings.preferences.locationInOffice', 'In-office') },
          { v: 'hybrid', l: t('views.accountSettings.preferences.locationHybrid', 'Hybrid') },
        ];
        const noticePeriodOptions = [
          { label: t('views.accountSettings.preferences.noticeImmediate', 'Immediate'), value: 'immediate' },
          { label: t('views.accountSettings.preferences.notice15Days', '15 days'), value: '15_days' },
          { label: t('views.accountSettings.preferences.notice1Month', '1 month'), value: '1_month' },
          { label: t('views.accountSettings.preferences.notice2Months', '2 months'), value: '2_months' },
          { label: t('views.accountSettings.preferences.notice3Months', '3 months'), value: '3_months' },
          { label: t('views.accountSettings.preferences.notice6Months', '6 months'), value: '6_months' },
        ];
        const industryOptions = [
          { v: 'software', l: t('views.accountSettings.preferences.industry.software', 'Software & IT') },
          { v: 'data_ai', l: t('views.accountSettings.preferences.industry.data_ai', 'Data & AI') },
          { v: 'design', l: t('views.accountSettings.preferences.industry.design', 'Design') },
          { v: 'product', l: t('views.accountSettings.preferences.industry.product', 'Product') },
          { v: 'marketing', l: t('views.accountSettings.preferences.industry.marketing', 'Marketing') },
          { v: 'fintech', l: t('views.accountSettings.preferences.industry.fintech', 'Fintech') },
          { v: 'healthcare', l: t('views.accountSettings.preferences.industry.healthcare', 'Healthcare') },
          { v: 'edtech', l: t('views.accountSettings.preferences.industry.edtech', 'EdTech') },
          { v: 'gaming', l: t('views.accountSettings.preferences.industry.gaming', 'Gaming') },
          { v: 'other', l: t('views.accountSettings.preferences.industry.other', 'Other') },
        ];
        const genderOptions = [
          { label: t('views.accountSettings.preferences.genderMan', 'Man'), value: 'man' },
          { label: t('views.accountSettings.preferences.genderWoman', 'Woman'), value: 'woman' },
          { label: t('views.accountSettings.preferences.genderNonBinary', 'Non-binary'), value: 'non_binary' },
          { label: t('views.accountSettings.preferences.genderPreferNot', 'Prefer not to say'), value: '' },
        ];

        return (
          <div key={i18n.language}>
            {errorToast}
            {formBanner('preferences')}
            <div className="w-full pb-10 mt-4">

              {/* ── Availability ──────────────────────────────────────────── */}
              <PrefWorkRow
                label={t('views.accountSettings.preferences.availability', 'Availability')}
                desc={t('views.accountSettings.preferences.availabilityDesc', "Let recruiters know if you're open to new opportunities.")}
              >
                <div className="ds-option-list">
                  {availabilityOptions.map(opt => (
                    <label
                      key={opt.val}
                      className="ds-option-row"
                      onClick={() => patchDraft({ jobSearchStatus: opt.val })}
                    >
                      <span className={`ds-radio-mark ${jss === opt.val ? 'is-selected' : ''}`} />
                      <div>
                        <p className="t-body t-strong">{opt.title}</p>
                        <p className="t-meta mt-0.5">{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </PrefWorkRow>

              {/* ── Preferred roles ───────────────────────────────────────── */}
              <PrefWorkRow
                label={t('views.accountSettings.preferences.preferredRoles', 'Preferred roles')}
                desc={t('views.accountSettings.preferences.preferredRolesDesc', "Up to 5 job titles you're actively targeting.")}
              >
                <div>
                  {preferredRoles.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {preferredRoles.map(r => (
                        <span key={r} className="inline-flex items-center gap-1 t-meta px-2.5 py-1 rounded-full font-medium"
                          style={{ background: 'var(--ds-bg-recessed)', border: '1px solid var(--ds-border)', color: 'var(--ds-text-body)' }}>
                          {r}
                          <button type="button" onClick={() => setPreferredRoles(prev => prev.filter(x => x !== r))}
                            className="t-tag hover:text-red-400 ml-0.5 transition-colors" style={{ color: 'var(--ds-text-disabled)' }}>✕</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="ds-field-control">
                    <input
                      type="text"
                      value={preferredRoleInput}
                      onChange={e => setPreferredRoleInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRole(); } }}
                      placeholder={t('views.accountSettings.preferences.preferredRolesPlaceholder', 'e.g. Product Manager, UX Designer…')}
                      className="ds-field-input"
                    />
                    <span className="ds-field-suffix t-tag" style={{ color: preferredRoles.length >= 5 ? 'var(--ds-danger)' : 'var(--ds-text-disabled)' }}>
                      {preferredRoles.length}/5
                    </span>
                  </div>
                  <p className="t-tag" style={{ color: 'var(--ds-text-disabled)', marginTop: '4px' }}>
                    {t('views.accountSettings.preferences.pressEnterToAdd', 'Press Enter to add each role')}
                  </p>
                </div>
              </PrefWorkRow>

              {/* ── Job type ──────────────────────────────────────────────── */}
              <PrefWorkRow
                label={t('views.accountSettings.preferences.jobType', 'Job type')}
                desc={t('views.accountSettings.preferences.jobTypeDesc', "Select up to 3 types of work you're open to.")}
              >
                <div className="ds-choice-group">
                  {jobTypeOptions.map(({ v, l }) => {
                    const sel = draft.preferredJobTypes.includes(v);
                    return (
                      <Pill key={v} label={l} selected={sel} onClick={() => {
                        const cur = draft.preferredJobTypes;
                        patchDraft({ preferredJobTypes: sel ? cur.filter(x => x !== v) : cur.length < 3 ? [...cur, v] : cur });
                      }} />
                    );
                  })}
                </div>
              </PrefWorkRow>

              {/* ── Location ──────────────────────────────────────────────── */}
              <PrefWorkRow
                label={t('views.accountSettings.preferences.workLocation', 'Work location')}
                desc={t('views.accountSettings.preferences.workLocationDesc', 'How and where you prefer to work. Add up to 5 cities.')}
              >
                <div>
                  <div className="ds-choice-group mb-3">
                    {locationModeOptions.map(({ v, l }) => (
                      <label key={v} className="flex items-center gap-2 cursor-pointer t-body" style={{ color: 'var(--ds-text-body)' }}>
                        <span
                          className={`ds-checkbox-mark ${locationModes.includes(v) ? 'is-selected' : ''}`}
                          onClick={() => toggleLocationMode(v)}
                        >
                          {locationModes.includes(v) && <Check className="w-2.5 h-2.5 text-white" />}
                        </span>
                        {l}
                      </label>
                    ))}
                  </div>
                  <LocationSearch
                    selected={selectedLocations}
                    onChange={locs => { setSelectedLocations(locs); patchDraft({ location: locs.join(', ') }); }}
                    max={5}
                  />
                </div>
              </PrefWorkRow>

              {/* ── Notice & offers ───────────────────────────────────────── */}
              <PrefWorkRow
                label={t('views.accountSettings.preferences.availabilityDetails', 'Availability details')}
                desc={t('views.accountSettings.preferences.availabilityDetailsDesc', 'Notice period and current offer status.')}
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block t-body mb-1.5">{t('views.accountSettings.preferences.noticePeriod', 'Notice period')}</label>
                    <Field label="" value={noticePeriod} onChange={setNoticePeriod}
                      select={noticePeriodOptions}
                    />
                  </div>
                  <div>
                    <label className="block t-body mb-1.5">{t('views.accountSettings.preferences.holdingOffers', 'Holding other offers?')}</label>
                    <SegmentedToggle
                      value={holdingOffers}
                      onChange={setHoldingOffers}
                    />
                  </div>
                </div>
              </PrefWorkRow>

              {/* ── Compensation ──────────────────────────────────────────── */}
              <PrefWorkRow
                label={t('views.accountSettings.preferences.compensation', 'Compensation')}
                desc={t('views.accountSettings.preferences.compensationDesc', 'Kept strictly private. Helps us filter out mismatched roles.')}
              >
                <div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <SettingsSalaryInput
                      label={t('views.accountSettings.preferences.currentSalary', 'Current salary (CTC)')}
                      value={currentSalary}
                      onChange={setCurrentSalary}
                      placeholder={t('views.accountSettings.preferences.currentSalaryPlaceholder', '12,00,000')}
                      currency={salaryCurrency}
                      onCurrencyChange={setSalaryCurrency}
                    />
                    <SettingsSalaryInput
                      label={t('views.accountSettings.preferences.expectedSalary', 'Expected salary (CTC)')}
                      value={draft.salaryExpectationInr}
                      onChange={v => patchDraft({ salaryExpectationInr: v })}
                      placeholder={t('views.accountSettings.preferences.expectedSalaryPlaceholder', '21,00,000')}
                      currency={salaryCurrency}
                      onCurrencyChange={setSalaryCurrency}
                    />
                  </div>
                  <p className="t-tag t-disabled mt-1.5">
                    {t('views.accountSettings.preferences.compensationPrivacy', 'We DO NOT share this with anyone. It only helps us filter out roles outside your range.')}
                  </p>
                </div>
              </PrefWorkRow>

              {/* ── Preferred industries ──────────────────────────────────── */}
              <PrefWorkRow
                label={t('views.accountSettings.preferences.preferredIndustries', 'Preferred industries')}
                desc={t('views.accountSettings.preferences.preferredIndustriesDesc', "Sectors and markets you're interested in working in.")}
              >
                <div className="flex flex-wrap gap-2">
                  {industryOptions.map(({ v, l }) => (
                    <Pill key={v} label={l} selected={draft.industry === v} onClick={() => patchDraft({ industry: draft.industry === v ? null : v })} />
                  ))}
                </div>
              </PrefWorkRow>

              {/* ── Company size ──────────────────────────────────────────── */}
              <PrefWorkRow
                label={t('views.accountSettings.preferences.companySize', 'Company size')}
                desc={t('views.accountSettings.preferences.companySizeDesc', 'Your preferred team or company scale. Up to 5.')}
              >
                <div className="flex flex-wrap gap-2">
                  {[
                    { v: '1_10', l: '1–10' }, { v: '11_50', l: '11–50' },
                    { v: '51_200', l: '51–200' }, { v: '201_500', l: '201–500' },
                    { v: '501_1000', l: '501–1,000' }, { v: '1000+', l: '1,000+' },
                  ].map(({ v, l }) => {
                    const sel = companySizePrefs.includes(v);
                    return (
                      <Pill key={v} label={l} selected={sel} onClick={() => { if (companySizePrefs.length < 5 || sel) toggleCompanySize(v); }} />
                    );
                  })}
                </div>
              </PrefWorkRow>

              {/* ── Identity ──────────────────────────────────────────────── */}
              <PrefWorkRow
                label={t('views.accountSettings.preferences.identity', 'Identity')}
                desc={t('views.accountSettings.preferences.identityDesc', 'Optional. Handled with the highest care and never shared.')}
                noDivider
              >
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field
                    label={t('views.accountSettings.preferences.genderIdentity', 'Gender identity')}
                    value={genderIdentity}
                    onChange={setGenderIdentity}
                    select={genderOptions}
                  />
                  <Field
                    label={t('views.accountSettings.preferences.dateOfBirth', 'Date of birth')}
                    type="date"
                    value={dob}
                    onChange={setDob}
                    hint={t('views.accountSettings.preferences.dobHint', "We won't send birthday notifications to your network.")}
                  />
                </div>
              </PrefWorkRow>

              <SettingsSaveFooter step={3} isSaving={isSaving} onSave={saveSection} onCancel={cancelChanges} t={t} />
            </div>
          </div>
        );
      }

      // ── Resume: work history, education, skills ───────────────────────────────
      case 'resume':
        return (
          <div key={i18n.language}>
          <SettingsTabCanvas className="pb-10">
            <SettingsTextHeader
              title={t('views.accountSettings.resume.title', 'Resume')}
              description={t('views.accountSettings.resume.desc', 'Your work history, education and skills.')}
            />

            {errorToast}

            <div
              className="rounded-xl border overflow-hidden"
              style={{ borderColor: 'var(--ds-border-strong)', background: 'var(--ds-bg-recessed)' }}
            >
              <ProfileResumeFields
                idPrefix="acct-resume"
                draft={draft}
                errors={sectionErrors[5]}
                onPatch={patchDraft}
              />
            </div>

            <SettingsSaveFooter step={5} isSaving={isSaving} onSave={saveSection} onCancel={cancelChanges} t={t} />
          </SettingsTabCanvas>
          </div>
        );

      // ── Security: password + account management ───────────────────────────────
      case 'notifications':
        return (
          <div key={i18n.language}>
          <SettingsTabCanvas>
            <SettingsTextHeader
              title={t('views.accountSettings.notifications.title', 'Notifications')}
              description={t('views.accountSettings.notifications.desc', 'Choose which emails you receive from GoGrabJob.')}
            />
            <NotificationsSettingsPanel highlightType={notificationHighlight} unsubError={unsubError} />
          </SettingsTabCanvas>
          </div>
        );

      case 'security':
        return (
          <div key={i18n.language}>
          <SettingsTabCanvas>
            <SettingsTextHeader
              title={t('views.accountSettings.security.title', 'Security')}
              description={t('views.accountSettings.security.desc', 'Password and account management.')}
            />
            <SettingsTabRow
              label={t('views.accountSettings.security.changePassword', 'Change password')}
              desc={t('views.accountSettings.security.changePasswordDesc', 'Update your account password. Leave current empty if you signed in via OAuth.')}
            >
              <PasswordChangeSection />
            </SettingsTabRow>
            <SettingsTabRow
              label={t('views.accountSettings.security.accountManagement', 'Account management')}
              desc={t('views.accountSettings.security.accountManagementDesc', 'Export a copy of your data or permanently delete your account.')}
              wide
              noBorder
            >
              <AccountManagementSection userEmail={user?.email ?? ''} onSignOut={() => void signOut?.()} />
            </SettingsTabRow>
          </SettingsTabCanvas>
          </div>
        );

      case 'tour':
        return (
          <SettingsTabCanvas>
            <SettingsTextHeader title={SECTION_META.tour.title} description={SECTION_META.tour.desc} />
            <SettingsTabRow
              label="Guided walkthrough"
              desc="Start or replay the interactive tour to learn every workspace tool — Jobs, Optimizer, AI Agent, Skill Assessment, and more."
              noBorder
            >
              <TourSubTab
                tourCompleted={tourProfile?.tourCompleted ?? false}
                onStart={() => {
                  window.dispatchEvent(new CustomEvent(FORCE_PRODUCT_TOUR_EVENT));
                }}
              />
            </SettingsTabRow>
          </SettingsTabCanvas>
        );

      // ── API Keys (admin-only) ─────────────────────────────────────────────────
      case 'api-keys':
        if (!isAdmin) return null;
        return (
          <SettingsTabCanvas>
            <SettingsTextHeader
              title="API Keys & Integrations"
              description="Personal access tokens, AI models and job channel connections."
            />
            <ApiKeysIntegrations isAdmin={isAdmin} />
          </SettingsTabCanvas>
        );

      // ── Billing ───────────────────────────────────────────────────────────────
      case 'billing':
        return (
          <div key={i18n.language}>
          <SettingsTabCanvas>
            <SettingsTextHeader
              title={t('views.accountSettings.billing.title', 'Billing')}
              description={t('views.accountSettings.billing.desc', 'Subscription, credits and payment history.')}
            />
            <SettingsTabRow
              label={t('views.accountSettings.billing.plansLabel', 'Plans')}
              desc={t(
                'views.accountSettings.billing.plansDesc',
                'Choose a plan that fits your needs. Upgrade anytime — secure payment via Cashfree.',
              )}
              uncapped
              noBorder
            >
              <Subscription />
            </SettingsTabRow>
          </SettingsTabCanvas>
          </div>
        );

      // ── Payment History ────────────────────────────────────────────────────────
      case 'payment-history':
        return (
          <div key={i18n.language}>
            <PaymentHistoryPage />
          </div>
        );

      // ── Admin sections ─────────────────────────────────────────────────────────
      // All admin sections are rendered persistently below (mounted once, then
      // hidden) so switching between tabs never remounts/refetches them.
      case 'admin-analytics':
      case 'control-panel':
      case 'cron-jobs-admin':
      case 'testimonials-admin':
      case 'product-feedback-admin':
      case 'translations-admin':
      case 'blog-cms':
      case 'careers-cms':
      case 'jobs-board':
        return null;

      default: return null;
    }
  };

  return (
    <div
      data-tour="page-account-settings"
      className="ds-page ds-account-settings flex flex-1 min-h-0 w-full overflow-hidden border-t flex-col md:flex-row"
      style={{ borderColor: 'var(--ds-border)' }}
    >
      {/* ── Mobile section picker (native dropdown) ─────────────────────── */}
      <div
        className="md:hidden shrink-0 border-b px-3 py-3"
        style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)' }}
      >
        <div className="ds-account-settings__mobile-tab-select">
          <label htmlFor="settings-tab-select" className="sr-only">
            Settings section
          </label>
          <select
            id="settings-tab-select"
            value={activeSection}
            onChange={(e) => selectSection(e.target.value as SettingsSection)}
            className="ds-account-settings__mobile-tab-select-native touch-manipulation"
            aria-label="Settings section"
          >
            <optgroup label="Settings">
              {visibleNav.filter((n) => !n.adminOnly).map(({ id, label, labelKey, shortLabel }) => (
                <option key={id} value={id}>
                  {labelKey ? t(labelKey, shortLabel ?? label) : (shortLabel ?? label)}
                </option>
              ))}
            </optgroup>
            {visibleNav.some((n) => n.adminOnly) && (
              <optgroup label="Admin">
                {visibleNav.filter((n) => n.adminOnly).map(({ id, label, labelKey, shortLabel }) => (
                  <option key={id} value={id}>
                    {labelKey ? t(labelKey, shortLabel ?? label) : (shortLabel ?? label)}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <ChevronDown className="ds-account-settings__mobile-tab-select-icon" aria-hidden />
        </div>
      </div>

      {/* ── Left nav rail (tablet/desktop) ─────────────────────────────── */}
      <aside
        data-tour="page-account-settings-nav"
        className={`hidden md:flex shrink-0 border-r flex-col overflow-y-auto overflow-x-hidden transition-[width] duration-200 ${
          sidebarCollapsed ? 'w-[56px] min-w-[56px] max-w-[56px]' : 'w-[220px] min-w-[220px] max-w-[220px]'
        }`}
        style={{
          borderColor: 'var(--ds-border)',
          background: 'var(--ds-bg-card)',
          color: 'var(--ds-text-body)',
        }}
      >
        <div className={`flex items-center py-2 ${sidebarCollapsed ? 'justify-center px-0' : 'justify-end px-3'}`}>
          <button
            type="button"
            onClick={toggleSidebar}
            className="flex items-center justify-center w-9 h-9 rounded-lg touch-manipulation"
            style={{ color: 'var(--ds-text-muted)' }}
            aria-label={sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={sidebarCollapsed ? 'Expand' : 'Collapse'}
          >
            {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>
        <nav className="flex-1 pb-3" aria-label="Settings navigation">
          {visibleNav.map(({ id, label, labelKey, icon: Icon, dividerBefore, adminOnly }) => {
            const isActive = activeSection === id;
            const navLabel = labelKey ? t(labelKey, label) : label;
            return (
              <React.Fragment key={id}>
                {dividerBefore && (
                  <div className={`my-2 border-t ${sidebarCollapsed ? 'mx-3' : 'mx-5'}`} style={{ borderColor: 'var(--ds-border)' }} />
                )}
                <button
                  type="button"
                  onClick={() => selectSection(id as SettingsSection)}
                  aria-current={isActive ? 'page' : undefined}
                  title={sidebarCollapsed ? navLabel : undefined}
                  className={`ds-workspace-nav-item ${isActive ? 'is-active' : ''} flex items-center min-h-[44px] py-2.5 text-left touch-manipulation ${
                    sidebarCollapsed ? 'justify-center px-0 gap-0' : 'gap-3 px-5'
                  }`}
                  style={{
                    borderLeft: isActive ? '2px solid var(--ds-brand)' : '2px solid transparent',
                  }}
                >
                  <Icon className="w-4 h-4 shrink-0" aria-hidden />
                  {!sidebarCollapsed && navLabel}
                  {!sidebarCollapsed && adminOnly && (
                    <span className="ml-auto t-tag font-medium px-1.5 py-0.5 rounded"
                      style={{ background: 'rgba(245,158,11,0.12)', color: 'var(--ds-warn)' }}>Admin</span>
                  )}
                </button>
              </React.Fragment>
            );
          })}
        </nav>
      </aside>

      {/* ── Content column ───────────────────────────────────────────────── */}
      <main
        data-tour="account-settings-content"
        className="flex-1 min-w-0 overflow-y-auto"
        style={{ background: 'var(--ds-bg-card)' }}
      >
        <div className="w-full px-3 sm:px-6 md:px-8 py-4 sm:py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          {isAdmin && controlPanelMounted && (
            <div
              className={activeSection === 'control-panel' ? '' : 'hidden'}
              aria-hidden={activeSection !== 'control-panel'}
            >
              <React.Suspense fallback={<AdminSectionFallback />}>
                <ControlPanel embedded parentIsAdmin isVisible={activeSection === 'control-panel'} />
              </React.Suspense>
            </div>
          )}

          {/* Other admin sections — mounted once on first visit, then kept alive
              (hidden) so tab switches never remount/refetch (no reload flash). */}
          {isAdmin && ADMIN_PERSISTENT_SECTIONS.map(sec => (
            visitedAdminSections.has(sec.id) ? (
              <div
                key={sec.id}
                className={activeSection === sec.id ? '' : 'hidden'}
                aria-hidden={activeSection !== sec.id}
              >
                <React.Suspense fallback={<AdminSectionFallback />}>
                  {sec.render(activeSection === sec.id)}
                </React.Suspense>
              </div>
            ) : null
          ))}

          {!ADMIN_SECTION_IDS.has(activeSection) && renderContent()}
        </div>
      </main>

    </div>
  );
};

export default AccountSettingsPage;
